import type { EscenarioPrueba } from '../types/pruebas';

/**
 * Catálogo de escenarios de prueba MANUAL de la teleconsulta MINSA.
 * Cubre de punta a punta: registro/autenticación, gestión de médicos y salas,
 * configuración de capacidad (fija/dinámica, horizonte), horarios, el ciclo
 * completo de atención (app móvil ↔ portal), cuelgue/desconexión, grabación,
 * notificaciones y una prueba de concurrencia básica.
 *
 * Contenido ESTÁTICO — el resultado (pass/fail + causa) vive en Firestore
 * (`pruebas_resultados/{id}`), un doc por escenario, sobrescrito en cada
 * corrida (sin historial). Ver `src/types/pruebas.ts`.
 *
 * Orden de las áreas = orden de aparición en la lista.
 */

const ADMIN = 'Administrador (portal)' as const;
const SUPERVISOR = 'Supervisor (portal)' as const;
const MEDICO = 'Médico (portal)' as const;
const NOTIFICADOR = 'Gestión de notificaciones (portal)' as const;
const SIN_PREREGISTRO = 'Persona sin pre-registro (portal)' as const;
const PACIENTE = 'Paciente (app móvil)' as const;
const SISTEMA = 'Sistema (automático)' as const;

export const AREAS_ORDEN = [
  'Registro, autenticación y cuentas',
  'Gestión de médicos y salas',
  'Configuración de salas',
  'Configuración global, regiones, monitor y dashboard',
  'App móvil — triage y admisión a la cola',
  'Ciclo de atención (médico ↔ paciente)',
  'Cuelgue y desconexión',
  'Finalización y resultado en el móvil',
  'Notificaciones',
  'Carga',
] as const;

export const ESCENARIOS: EscenarioPrueba[] = [
  // ───────────────────────────────────────────────────────────────────────
  // A. Registro, autenticación y cuentas
  // ───────────────────────────────────────────────────────────────────────
  {
    id: 'ESC-01',
    area: 'Registro, autenticación y cuentas',
    titulo: 'Pre-registro de usuarios del portal (médico/supervisor/admin/notificador)',
    objetivo:
      'Verificar que un administrador puede pre-registrar usuarios del PORTAL WEB de MinSalud (médico, supervisor, administrador o gestión de notificaciones — NO pacientes de la app móvil), y que la persona invitada puede crear su cuenta y acceder al portal con el rol correcto.',
    configuracionInicial: [
      'Sesión iniciada como Administrador en el portal.',
      'Un correo institucional de prueba que NO tenga cuenta previa en el sistema.',
    ],
    pasos: [
      { actor: ADMIN, accion: 'Ir a Usuarios → "+ Pre-registrar usuario".' },
      { actor: ADMIN, accion: 'Completar nombre y correo institucional, y elegir el rol "Gestión de notificaciones" (probar también con médico/supervisor/administrador si se desea cubrir los 4).' },
      { actor: ADMIN, accion: 'Guardar y verificar que aparece en la lista de usuarios pre-registrados pendientes ("Recibirá una invitación para crear su cuenta").' },
      { actor: SUPERVISOR, accion: 'La persona invitada abre el PORTAL (no la app móvil) y va a "¿Nuevo en el sistema? Solicita acceso" → pantalla de Registro.' },
      { actor: SUPERVISOR, accion: 'Ingresar el MISMO correo institucional pre-registrado, definir una contraseña que cumpla los requisitos, y confirmarla.' },
      { actor: SISTEMA, accion: 'Enviar un enlace de verificación a ese correo.' },
      { actor: SUPERVISOR, accion: 'Abrir el enlace de verificación desde el correo para activar la cuenta.' },
      { actor: SUPERVISOR, accion: 'Iniciar sesión en el portal con el correo y la contraseña recién creados.' },
      { actor: ADMIN, accion: 'Verificar en Usuarios que la cuenta pasó de "Pre-registrado" a activa, con el rol asignado correcto.' },
    ],
    checkpoints: [
      'Solo se puede completar el registro en el PORTAL con un correo que YA fue pre-registrado (si el correo no está pre-registrado, el registro debe rechazarse — ver ESC-04).',
      'El rol elegido en el pre-registro (médico / supervisor / administrador / gestión de notificaciones) es el mismo con el que la cuenta queda operando en el portal (custom claim correcto).',
      'El Administrador puede pre-registrar los 4 roles, incluido Administrador (el Supervisor puede los mismos 4 EXCEPTO Administrador — ver la matriz completa en ESC-03).',
      'La cuenta "Gestión de notificaciones" ve únicamente el módulo de Campañas/notificaciones — no atiende pacientes ni requiere colegiado/especialidad/salas.',
      'Este flujo es exclusivo del portal web de staff — no crea ni afecta ninguna cuenta de paciente de la app móvil.',
    ],
    notas: 'No confundir con la app móvil de pacientes (otro sistema, con su propio auto-registro por cédula sin invitación de un admin — no cubierto en esta área). La validación de identidad del paciente en el móvil se cubre en ESC-07, dentro del área "App móvil — triage y admisión a la cola".',
  },
  {
    id: 'ESC-02',
    area: 'Registro, autenticación y cuentas',
    titulo: 'Pre-registro de médico (por Supervisor)',
    objetivo:
      'Verificar que el SUPERVISOR (rol operativo que gestiona médicos y salas) puede pre-registrar un médico (colegiado, especialidad y salas) en el PORTAL WEB, y que el médico invitado puede crear su acceso (solo correo + contraseña) desde la pantalla de Login → "Solicita acceso" y queda operativo con su sala asignada.',
    configuracionInicial: [
      'Sesión iniciada como Supervisor (el Administrador también puede hacerlo — ver matriz de permisos en ESC-03 — pero el Supervisor es el rol natural para esta gestión operativa).',
      'Al menos una sala creada para asignar al médico de prueba.',
      'Un correo institucional de prueba que NO tenga cuenta previa en el sistema.',
    ],
    pasos: [
      { actor: SUPERVISOR, accion: 'Ir a Usuarios → "+ Pre-registrar usuario" → rol "Médico" → completar nombre, correo, colegiado, especialidad y sala(s) asignada(s).' },
      { actor: SUPERVISOR, accion: 'Guardar y verificar que aparece en la lista de usuarios pre-registrados pendientes, con su especialidad y sala correctas.' },
      { actor: MEDICO, accion: 'Ir a la pantalla de Login del portal y elegir "¿Nuevo en el sistema? Solicita acceso".' },
      { actor: MEDICO, accion: 'En la pantalla de Registro, completar ÚNICAMENTE el correo institucional pre-registrado y definir una contraseña (con su confirmación) — no se piden nombre, colegiado ni otros datos, porque ya vienen del pre-registro.' },
      { actor: SISTEMA, accion: 'Enviar un enlace de verificación al correo.' },
      { actor: MEDICO, accion: 'Abrir el enlace de verificación desde el correo para activar la cuenta.' },
      { actor: MEDICO, accion: 'Iniciar sesión en el portal con el correo y la contraseña recién creados.' },
      { actor: MEDICO, accion: 'Verificar que ve la sala asignada en "Mis salas" y que su especialidad/colegiado aparecen correctos en su perfil.' },
    ],
    checkpoints: [
      'Un Supervisor SÍ puede pre-registrar un médico (permiso habilitado; antes era exclusivo de Administrador).',
      'El médico pre-registrado tiene el rol "medico" correcto (custom claim), sin que él haya podido elegirlo.',
      'El registro (Login → "Solicita acceso") solo pide correo y contraseña — SIN volver a pedir nombre, colegiado, especialidad ni salas (esos datos ya quedaron fijados en el pre-registro).',
      'Un correo que NO fue pre-registrado no puede completar el registro.',
      'El médico ve únicamente las salas que le fueron asignadas.',
      'La especialidad y el colegiado se muestran correctamente en su perfil, tal como los definió el supervisor.',
    ],
  },
  {
    id: 'ESC-03',
    area: 'Registro, autenticación y cuentas',
    titulo: 'Pre-registro de supervisor y administrador — límites por rol',
    objetivo:
      'Verificar la matriz de creación de usuarios: el Administrador puede pre-registrar CUALQUIER rol (médico/supervisor/administrador/notificador); el Supervisor puede pre-registrar cualquier rol EXCEPTO Administrador (debe ser rechazado).',
    configuracionInicial: [
      'Sesión iniciada como Supervisor y, por separado, sesión como Administrador.',
      'Dos correos institucionales de prueba que NO tengan cuenta previa en el sistema (uno para el nuevo Supervisor, otro para el nuevo Administrador).',
    ],
    pasos: [
      { actor: SUPERVISOR, accion: 'Ir a Usuarios → "+ Pre-registrar usuario" → elegir rol "Supervisor" → completar nombre y el primer correo → guardar.' },
      { actor: SUPERVISOR, accion: 'Verificar que en el formulario de pre-registro la opción "Administrador" NO aparece disponible para elegir (solo Médico, Supervisor, Gestión de notificaciones).' },
      { actor: SUPERVISOR, accion: '(Si se fuerza el envío por API/consola con role="admin" y el segundo correo) verificar que el backend RECHAZA con "El supervisor no puede pre-registrar administradores".' },
      { actor: ADMIN, accion: 'Con la cuenta Administrador, ir a "+ Pre-registrar usuario" → confirmar que SÍ ve la opción "Administrador" → elegir ese rol con el segundo correo → guardar.' },
      { actor: SUPERVISOR, accion: 'Ir a Login → "¿Nuevo en el sistema? Solicita acceso" → completar SOLO el primer correo (el pre-registrado como Supervisor) y una contraseña (con confirmación) → verificar el enlace de correo → activar → iniciar sesión.' },
      { actor: SUPERVISOR, accion: 'Verificar el menú visible de esta cuenta nueva (todo salvo Prompts IA/Configurar IA y Pruebas).' },
      { actor: ADMIN, accion: 'Repetir el mismo flujo de Login → "Solicita acceso" con el segundo correo (rol Administrador) → iniciar sesión.' },
      { actor: ADMIN, accion: 'Verificar el menú visible de esta cuenta nueva (todo salvo Salas/Historial clínico).' },
    ],
    checkpoints: [
      'El formulario de pre-registro NO muestra la opción "Administrador" cuando quien pre-registra es Supervisor (oculta en la UI).',
      'Aunque se intente forzar por API, el backend rechaza a un Supervisor pre-registrando role="admin" con permission-denied.',
      'El Administrador SÍ puede pre-registrar los 4 roles, incluido Administrador.',
      'El registro (Login → "Solicita acceso") solo pide correo y contraseña en ambos casos — el rol ya viene fijado por el pre-registro.',
      'La cuenta Supervisor nueva NO ve "Inteligencia Artificial" (Prompts IA / Configurar IA) ni "Pruebas".',
      'La cuenta Administrador nueva NO ve "Salas" ni "Historial" clínico en el menú de Atención clínica.',
    ],
  },
  {
    id: 'ESC-04',
    area: 'Registro, autenticación y cuentas',
    titulo: 'Registro SIN pre-registro previo (debe rechazarse)',
    objetivo:
      'Verificar que el PORTAL WEB no permite completar el registro (Login → "Solicita acceso") con un correo que NUNCA fue pre-registrado por un administrador — el pre-registro es un requisito obligatorio, no un formulario libre.',
    configuracionInicial: [
      'Un correo institucional de prueba que NO tenga ningún pre-registro ni cuenta previa en el sistema (confirmar en Usuarios que no aparece).',
    ],
    pasos: [
      { actor: SIN_PREREGISTRO, accion: 'Ir a la pantalla de Login del portal y elegir "¿Nuevo en el sistema? Solicita acceso".' },
      { actor: SIN_PREREGISTRO, accion: 'Completar el correo (el que NO tiene pre-registro) y definir una contraseña con su confirmación.' },
      { actor: SIN_PREREGISTRO, accion: 'Intentar enviar el formulario de registro.' },
      { actor: SISTEMA, accion: 'Verificar que el registro es RECHAZADO con un mensaje claro (ej. "correo no autorizado" / "consulte con su administrador"), sin crear ninguna cuenta.' },
      { actor: ADMIN, accion: 'Confirmar en Usuarios que no se creó ningún usuario nuevo ni pendiente para ese correo.' },
    ],
    checkpoints: [
      'El registro se bloquea SIEMPRE que el correo no tenga un pre-registro válido creado por un admin.',
      'El mensaje de rechazo es claro y no revela información sensible del sistema.',
      'No queda ninguna cuenta de Firebase Auth ni documento huérfano creado para el intento fallido.',
    ],
  },
  {
    id: 'ESC-05',
    area: 'Registro, autenticación y cuentas',
    titulo: 'Cambio de contraseña (portal)',
    objetivo: 'Verificar el flujo de recuperación de contraseña del PORTAL vía código de 6 dígitos enviado al correo institucional (3 pasos: correo → código → nueva contraseña).',
    configuracionInicial: ['Cuenta de prueba existente (cualquier rol de portal) con correo institucional válido y accesible.'],
    pasos: [
      { actor: MEDICO, accion: 'En el Login del portal, elegir "¿Olvidaste tu contraseña?".' },
      { actor: MEDICO, accion: 'Paso 1 de 3: ingresar el correo institucional.' },
      { actor: SISTEMA, accion: 'Enviar un código de 6 dígitos a ese correo.' },
      { actor: MEDICO, accion: 'Paso 2 de 3: ingresar el código de 6 dígitos recibido.' },
      { actor: MEDICO, accion: 'Paso 3 de 3: definir y confirmar la nueva contraseña.' },
      { actor: MEDICO, accion: 'Cerrar sesión e iniciar sesión de nuevo en el portal con la nueva contraseña.' },
    ],
    checkpoints: [
      'El código llega al correo en un tiempo razonable (revisar también Spam).',
      'La nueva contraseña reemplaza a la anterior (la vieja deja de funcionar).',
      'Un código vencido o incorrecto es rechazado con un mensaje claro, sin avanzar de paso.',
    ],
  },
  {
    id: 'ESC-06',
    area: 'Registro, autenticación y cuentas',
    titulo: 'Ingreso por código de acceso (portal, sin contraseña)',
    objetivo: 'Verificar el login alternativo del PORTAL por código de un solo uso enviado al correo, sin necesidad de contraseña.',
    configuracionInicial: ['Cuenta de prueba existente (cualquier rol de portal) con correo institucional válido.'],
    pasos: [
      { actor: MEDICO, accion: 'En el Login del portal, cambiar el toggle de "Contraseña" a "Código de acceso".' },
      { actor: MEDICO, accion: 'Ingresar el correo institucional y solicitar el código.' },
      { actor: SISTEMA, accion: 'Enviar el código de 6 dígitos a ese correo ("sin necesidad de contraseña").' },
      { actor: MEDICO, accion: 'Ingresar el código y verificar que entra al portal sin usar contraseña.' },
      { actor: MEDICO, accion: 'Solicitar un reenvío de código y confirmar el cooldown/espera antes de poder reenviar.' },
    ],
    checkpoints: [
      'El login por código funciona y lleva al portal (con el rol/menú correctos) sin pedir contraseña.',
      'El reenvío respeta el tiempo de espera configurado (no permite spamear códigos).',
      'Un código expirado o ya usado es rechazado con un mensaje claro.',
    ],
  },
  {
    id: 'ESC-07',
    area: 'App móvil — triage y admisión a la cola',
    titulo: 'Auto-revalidación de identidad (biométrico y correo)',
    objetivo:
      'Verificar que un paciente "Pendiente" puede resolver su propia validación dentro de la app, sin cerrar sesión ni re-registrarse, por biométrico o por correo MINSA.',
    configuracionInicial: [
      'Cuenta de prueba con `isValidated: false` (paciente "Pendiente").',
      'Confirmar que en el Home aparece el banner/CTA de verificación pendiente.',
    ],
    pasos: [
      { actor: PACIENTE, accion: 'En el Home, tocar "Verificar con biometría".' },
      { actor: PACIENTE, accion: 'Completar cédula + selfie (liveness) en el flujo KYC.' },
      { actor: SISTEMA, accion: 'Al aprobar, escribir `kycBiometricVerified`/`isValidated` en la cuenta actual (sin ir a un registro nuevo).' },
      { actor: PACIENTE, accion: 'Verificar que vuelve al Home ya desbloqueado (badge "Verificado", sin candados).' },
      { actor: PACIENTE, accion: 'Repetir el escenario con OTRA cuenta Pendiente, esta vez usando "Verificar con mi correo" (PIN a correo MINSA).' },
      { actor: PACIENTE, accion: 'Si el correo MINSA no está disponible, confirmar que la app OFRECE el biométrico como alternativa (no un callejón sin salida).' },
    ],
    checkpoints: [
      'El biométrico por sí solo desbloquea TODAS las secciones (Gestión de Citas, Histórico, Mi Salud), sin depender del correo.',
      'La vía de correo funciona cuando MINSA tiene/coincide el correo de la cédula.',
      'Si el correo falla, se ofrece biométrico en vez de un mensaje de "acércate a un centro médico" directo.',
      'No hubo que cerrar sesión ni re-registrarse en ningún punto.',
    ],
  },

  // ───────────────────────────────────────────────────────────────────────
  // B. Gestión de médicos y salas
  // ───────────────────────────────────────────────────────────────────────
  {
    id: 'ESC-08',
    area: 'Gestión de médicos y salas',
    titulo: 'Deshabilitar y habilitar médico',
    objetivo:
      'Verificar, ENTERAMENTE desde el portal MinSalud (sin depender de la app móvil), que deshabilitar a un médico lo excluye de la capacidad activa de la sala y que habilitarlo lo reincorpora.',
    configuracionInicial: [
      'Sesión iniciada como Supervisor (habilitar/deshabilitar médico usa el callable `gestionarMedicosSala`, EXCLUSIVO de `role === \'supervisor\'` — el Administrador ya no gestiona salas).',
      'Una sala con al menos 2 médicos habilitados.',
      'Sala abierta y dentro de horario.',
    ],
    cambiosConfiguracion: [
      'Deshabilitar a uno de los médicos desde Configurar salas → pestaña Médicos.',
    ],
    pasos: [
      { actor: SUPERVISOR, accion: 'En Configurar salas → pestaña Médicos, deshabilitar al médico A (botón "Deshabilitar").' },
      { actor: SUPERVISOR, accion: 'Verificar en Monitor en vivo que el médico A ya no cuenta como "en línea/disponible" para la capacidad de la sala.' },
      { actor: MEDICO, accion: 'El médico A inicia sesión en el portal y verifica en "Mis salas" que ya no puede operar en esa sala (o aparece como deshabilitado).' },
      { actor: SUPERVISOR, accion: 'Habilitar de nuevo al médico A desde Configurar salas → Médicos.' },
      { actor: SUPERVISOR, accion: 'Verificar en Monitor en vivo que el médico A vuelve a contar para la capacidad.' },
      { actor: MEDICO, accion: 'Confirmar que el médico A recupera acceso normal a la sala, sin reiniciar sesión.' },
    ],
    checkpoints: [
      'Con el médico deshabilitado, el conteo de médicos activos/en línea (visible en Monitor en vivo) lo excluye — verificable 100% desde el portal.',
      'El médico deshabilitado no puede operar en la sala desde el portal.',
      'Al re-habilitar, el médico recupera acceso normal sin necesidad de reiniciar sesión.',
    ],
    notas: 'El efecto sobre un paciente real que intenta entrar a la cola sin médicos disponibles se valida de punta a punta en ESC-26 (área "App móvil — triage y admisión a la cola"); este escenario se enfoca solo en la gestión desde el portal.',
  },
  {
    id: 'ESC-09',
    area: 'Gestión de médicos y salas',
    titulo: 'Habilitar y deshabilitar sala',
    objetivo: 'Verificar, ENTERAMENTE desde el portal MinSalud, que desactivar una sala la marca como no operativa y que reactivarla la restaura.',
    configuracionInicial: [
      'Sesión iniciada como Supervisor (el toggle "Activa" de la lista de salas guarda vía `actualizarSala`, EXCLUSIVO de `role === \'supervisor\'` — igual que `cambiarEstadoSala`, ver ESC-18).',
      'Una sala de prueba activa, con médicos y horario vigente.',
    ],
    cambiosConfiguracion: ['Cambiar "Activa" a NO en Configurar salas.'],
    pasos: [
      { actor: SUPERVISOR, accion: 'En Configurar salas, desactivar la sala (campo "Activa").' },
      { actor: SUPERVISOR, accion: 'Verificar que la sala aparece como inactiva en el listado de Configurar salas y que Monitor en vivo deja de mostrarla operativa.' },
      { actor: MEDICO, accion: 'El médico asignado a esa sala inicia sesión y verifica en "Mis salas" que aparece inactiva/no disponible.' },
      { actor: SUPERVISOR, accion: 'Reactivar la sala (campo "Activa" = sí).' },
      { actor: SUPERVISOR, accion: 'Verificar en Configurar salas y Monitor en vivo que vuelve a estado activo/operativo, sin esperar redeploy.' },
    ],
    checkpoints: [
      'El estado inactivo de la sala se refleja de inmediato en Configurar salas y Monitor en vivo — verificable 100% desde el portal.',
      'El médico asignado ve la sala como no operativa mientras está inactiva.',
      'Al reactivar, todo vuelve a la normalidad de inmediato (sin esperar redeploy).',
    ],
    notas: 'Este escenario se enfoca solo en la gestión desde el portal (activar/desactivar). Ningún otro escenario del catálogo prueba con un paciente real que una sala con "Activa"=No lo rechace al intentar entrar a la cola — si se quiere ese punta a punta, agregar un paso complementario con la app móvil.',
  },
  {
    id: 'ESC-10',
    area: 'Gestión de médicos y salas',
    titulo: 'Roles y accesos (matriz completa)',
    objetivo: 'Verificar la matriz de accesos por rol: Pruebas e Inteligencia Artificial son SOLO de Administrador; Configurar salas, Configuración, Fondos Virtuales y Regiones Teleconsulta son SOLO de Supervisor.',
    configuracionInicial: ['Una cuenta de cada rol disponible: Administrador, Supervisor, Médico, Notificador.'],
    pasos: [
      { actor: ADMIN, accion: 'Iniciar sesión y verificar acceso a: Dashboard, Monitor, Eventos, Usuarios, IA, Auditoría, Notificaciones y Pruebas — y verificar que Configurar salas, Configuración, Fondos Virtuales y Regiones Teleconsulta NO aparecen en su menú.' },
      { actor: ADMIN, accion: 'Con esta misma cuenta, intentar navegar directamente a `/config/salas` por URL y confirmar que es bloqueada (fail-closed).' },
      { actor: SUPERVISOR, accion: 'Iniciar sesión y verificar que ve: Dashboard, Monitor, Eventos, Usuarios, Configurar salas, Configuración, Fondos Virtuales, Regiones Teleconsulta, Auditoría — y que NO ve Inteligencia Artificial ni Pruebas.' },
      { actor: MEDICO, accion: 'Iniciar sesión y verificar que solo ve su Dashboard (filtrado), Salas e Historial.' },
      { actor: SUPERVISOR, accion: 'Intentar navegar directamente a `/admin/pruebas` por URL (sin pasar por el menú) y confirmar que es bloqueada (fail-closed).' },
    ],
    checkpoints: [
      'Ningún rol distinto de Administrador puede acceder a `/admin/pruebas` ni a Inteligencia Artificial, ni por menú ni por URL directa.',
      'Ningún rol distinto de Supervisor puede acceder a Configurar salas, Configuración, Fondos Virtuales ni Regiones Teleconsulta, ni por menú ni por URL directa (incluyendo el Administrador).',
      'El médico no ve pantallas administrativas.',
    ],
  },

  // ───────────────────────────────────────────────────────────────────────
  // C. Configuración de salas
  // ───────────────────────────────────────────────────────────────────────
  {
    id: 'ESC-11',
    area: 'Configuración de salas',
    titulo: 'Crear y configurar una sala',
    objetivo: 'Verificar la creación de una sala nueva con horario, tipo, descripción y región.',
    configuracionInicial: ['Sesión iniciada como Supervisor (el callable `crearSala` es EXCLUSIVO de `role === \'supervisor\'` — el Administrador ya no gestiona salas).'],
    pasos: [
      { actor: SUPERVISOR, accion: 'Ir a Configurar salas → Nueva sala.' },
      { actor: SUPERVISOR, accion: 'Completar nombre, tipo, descripción y elegir un atajo de horario (ej. Lun-Vie 08:00-17:00).' },
      { actor: SUPERVISOR, accion: 'Guardar y verificar que aparece en la lista con estado inicial "cerrada".' },
      { actor: SUPERVISOR, accion: 'Entrar a editar la sala → pestaña Horarios → confirmar que las reglas quedaron creadas.' },
    ],
    checkpoints: [
      'La sala se crea sin errores y aparece en el listado.',
      'El estado inicial es "cerrada" (no admite pacientes hasta habilitarla).',
      'Las reglas de horario del atajo elegido quedan correctamente en la subcolección.',
    ],
  },
  {
    id: 'ESC-12',
    area: 'Configuración de salas',
    titulo: 'Asignar y retirar médicos de la sala',
    objetivo: 'Verificar la asignación y retiro de médicos en una sala, y su efecto inmediato en la capacidad.',
    configuracionInicial: ['Sesión iniciada como Supervisor (el callable `gestionarMedicosSala` es EXCLUSIVO de `role === \'supervisor\'`). Una sala de prueba y 2 médicos de prueba disponibles.'],
    pasos: [
      { actor: SUPERVISOR, accion: 'Editar la sala → pestaña Médicos → "Asignar médico" → elegir médico A.' },
      { actor: SUPERVISOR, accion: 'Verificar que el médico A aparece habilitado en la lista de médicos de la sala.' },
      { actor: SUPERVISOR, accion: 'Retirar al médico A de la sala.' },
      { actor: SUPERVISOR, accion: 'Verificar que ya no aparece en la lista y que no cuenta para la capacidad de esa sala.' },
    ],
    checkpoints: [
      'Asignar/retirar médicos se refleja de inmediato en la lista de médicos de la sala.',
      'Un médico retirado deja de contar para el cálculo de capacidad.',
    ],
  },
  {
    id: 'ESC-13',
    area: 'Configuración de salas',
    titulo: 'Capacidad de atención + Vista previa',
    objetivo: 'Validar que la pestaña Capacidad y el panel "Vista previa" muestran cifras coherentes con la configuración real de la sala.',
    configuracionInicial: [
      'Sesión iniciada como Supervisor (la pestaña Capacidad guarda mediante `actualizarSala`, EXCLUSIVO de `role === \'supervisor\'`).',
      'Sala con 2 médicos habilitados, tiempo por consulta = 15 min.',
    ],
    pasos: [
      { actor: SUPERVISOR, accion: 'Ir a la pestaña Capacidad y anotar el cálculo mostrado (cupos/hora por médico, capacidad total).' },
      { actor: SUPERVISOR, accion: 'Ir a la pestaña "Vista previa" y comparar la capacidad estimada mostrada contra el cálculo de la pestaña Capacidad.' },
      { actor: SUPERVISOR, accion: 'Cambiar el tiempo por consulta (ej. de 15 a 20 min) y refrescar la Vista previa.' },
      { actor: SUPERVISOR, accion: 'Verificar que el número de cupos/hora baja de forma proporcional (60÷20 = 3 en vez de 60÷15 = 4).' },
    ],
    checkpoints: [
      'La cifra de "cupos por hora por médico" en Capacidad coincide con 60 ÷ tiempoEstimadoMin.',
      'La Vista previa refleja el mismo cálculo (no una cifra desincronizada).',
      'Cambiar el tiempo por consulta actualiza ambas pantallas de forma consistente.',
    ],
  },
  {
    id: 'ESC-14',
    area: 'Configuración de salas',
    titulo: 'Cupos fijos vs. cupos dinámicos',
    objetivo: 'Verificar el comportamiento de admisión bajo cada modo y que el tope máximo siempre es el techo absoluto.',
    configuracionInicial: [
      'Sala en modo "Cupos fijos", tope máximo = 3, 1 médico habilitado.',
      'Nota: el cambio de modo se guarda vía `actualizarSala` (EXCLUSIVO de `role === \'supervisor\'`).',
    ],
    cambiosConfiguracion: [
      'Cambiar el modo a "Cupos dinámicos" a mitad de la prueba.',
    ],
    pasos: [
      { actor: PACIENTE, accion: 'Con "Cupos fijos", llenar la cola hasta el tope (3) y verificar que el 4to paciente es rechazado.' },
      { actor: SUPERVISOR, accion: 'Cambiar el modo de la sala a "Cupos dinámicos" (mismo tope máximo = 3).' },
      { actor: PACIENTE, accion: 'Verificar que, aunque el cálculo dinámico dé una cifra mayor, nunca se admite más de 3 en cola (tope absoluto).' },
    ],
    checkpoints: [
      'En modo fijo, la sala admite exactamente hasta el tope configurado.',
      'En modo dinámico, el tope máximo sigue siendo un techo que NUNCA se supera.',
      'El cambio de modo aplica en caliente (sin esperar redeploy).',
    ],
  },
  {
    id: 'ESC-15',
    area: 'Configuración de salas',
    titulo: 'Horizonte de atención (cupos dinámicos)',
    objetivo: 'Verificar que el "horizonte de atención" (horas a considerar) cambia la capacidad admitida de forma proporcional.',
    configuracionInicial: [
      'Sesión iniciada como Supervisor (`horizonteCapacidadHoras` se guarda vía `actualizarSala`, EXCLUSIVO de `role === \'supervisor\'`).',
      'Sala en modo "Cupos dinámicos", 2 médicos en línea, tiempo por consulta 15 min, horizonte = 1 hora.',
    ],
    cambiosConfiguracion: [
      'Subir el horizonte de 1 hora a 6 horas.',
    ],
    pasos: [
      { actor: SUPERVISOR, accion: 'Con horizonte = 1h, anotar la capacidad mostrada (2 médicos × 4 cupos/h × 1h = 8).' },
      { actor: PACIENTE, accion: 'Llenar la cola hasta 8 y verificar que el siguiente es rechazado.' },
      { actor: SUPERVISOR, accion: 'Subir el horizonte a 6 horas y guardar.' },
      { actor: PACIENTE, accion: 'Verificar que ahora se admiten más pacientes (2 × 4 × 6 = 48, sujeto al tope máximo).' },
    ],
    checkpoints: [
      'La capacidad calculada coincide con médicos × cupos/hora × horizonte.',
      'El cambio de horizonte se refleja en la admisión real de pacientes (no solo en la pantalla).',
      'El tope máximo configurado sigue respetándose como techo absoluto.',
    ],
  },
  {
    id: 'ESC-16',
    area: 'Configuración de salas',
    titulo: 'Descontar médicos desconectados + tolerancia',
    objetivo: 'Verificar que, con la opción activada, un médico sin señal reciente deja de contar para la capacidad tras la ventana configurada.',
    configuracionInicial: [
      'Sala en modo "Cupos dinámicos", 2 médicos en línea.',
      'Activar "Descontar médicos desconectados" con tolerancia = 2 minutos (esta opción se guarda vía `actualizarSala`, EXCLUSIVO de Supervisor).',
    ],
    pasos: [
      { actor: MEDICO, accion: 'El médico B cierra la app / pierde conexión (deja de enviar heartbeat).' },
      { actor: SUPERVISOR, accion: 'Antes de que pasen los 2 minutos, verificar que el médico B AÚN cuenta para la capacidad.' },
      { actor: SUPERVISOR, accion: 'Tras superar los 2 minutos, verificar que el médico B YA NO cuenta (la capacidad baja).' },
      { actor: MEDICO, accion: 'El médico B reconecta y verificar que vuelve a contar.' },
    ],
    checkpoints: [
      'Dentro de la ventana de tolerancia, el médico sigue contando (evita perder cupos por microcortes).',
      'Al superar la ventana, la capacidad se recalcula sin el médico desconectado.',
      'Al reconectar, el médico se reincorpora al cálculo automáticamente.',
    ],
  },
  {
    id: 'ESC-17',
    area: 'Configuración de salas',
    titulo: 'Horarios: antes de abrir, dentro, después de cerrar, bloqueo de mediodía y buffer',
    objetivo: 'Verificar los distintos momentos del horario y su efecto en la admisión.',
    configuracionInicial: [
      'Sala con horario 08:00–12:00 y 13:00–15:00 (mediodía bloqueado), buffer de cierre = 15 min.',
    ],
    pasos: [
      { actor: PACIENTE, accion: 'Antes de las 08:00, solicitar un cupo → verificar el mensaje "La sala abre a las 08:00".' },
      { actor: PACIENTE, accion: 'Entre 08:00 y 12:00, solicitar un cupo → verificar que es admitido.' },
      { actor: PACIENTE, accion: 'Entre 12:00 y 13:00 (bloqueo de mediodía), solicitar un cupo → verificar el rechazo.' },
      { actor: PACIENTE, accion: 'Entre 14:45 y 15:00 (dentro del buffer de cierre), solicitar un cupo → verificar el rechazo por buffer.' },
      { actor: PACIENTE, accion: 'Después de las 15:00, solicitar un cupo → verificar "La sala ya cerró por hoy".' },
    ],
    checkpoints: [
      'Cada uno de los 5 momentos produce el resultado esperado (admite o rechaza con el mensaje correcto).',
      'El bloqueo de mediodía se respeta exactamente en el hueco entre franjas.',
      'El buffer de cierre bloquea nuevas admisiones en los minutos previos al cierre real.',
    ],
  },
  {
    id: 'ESC-18',
    area: 'Configuración de salas',
    titulo: 'Estados de sala (pausa / suspensión / cierre manual)',
    objetivo: 'Verificar que cada estado operativo de la sala bloquea la admisión con su mensaje correspondiente, y que reanudar restaura el flujo.',
    configuracionInicial: ['Sala abierta y dentro de horario, con médicos disponibles.'],
    cambiosConfiguracion: [
      'Cambiar el estado de la sala sucesivamente a: pausa, suspendida (día completo) y cerrada.',
    ],
    pasos: [
      { actor: SUPERVISOR, accion: 'Poner la sala en Pausa → paciente solicita cupo → verificar rechazo "La sala está en pausa".' },
      { actor: SUPERVISOR, accion: 'Reanudar la sala → registrar una suspensión de día completo (hoy) → paciente solicita cupo → verificar rechazo por suspensión.' },
      { actor: SUPERVISOR, accion: 'Eliminar la suspensión y cambiar el estado a "cerrada" manualmente → paciente solicita cupo → verificar rechazo.' },
      { actor: SUPERVISOR, accion: 'Volver el estado a "abierta" → paciente solicita cupo → verificar que es admitido.' },
    ],
    checkpoints: [
      'Cada estado (pausa, suspendida, cerrada) produce un rechazo con mensaje específico y coherente.',
      'Al reanudar/reabrir, la sala vuelve a admitir de inmediato.',
    ],
  },
  {
    id: 'ESC-19',
    area: 'Configuración de salas',
    titulo: 'Mensajes personalizados al paciente',
    objetivo: 'Verificar que las plantillas de mensaje configuradas por sala se reflejan tal cual en los rechazos que ve el paciente.',
    configuracionInicial: [
      'Sesión iniciada como Supervisor (la pestaña Mensajes guarda vía `actualizarSala`, EXCLUSIVO de `role === \'supervisor\'`).',
      'Sala de prueba. Ir a la pestaña "Mensajes" y personalizar al menos: "Cola llena — reintentar hoy" y "Sin médicos disponibles" con un texto distintivo (ej. "PRUEBA-MSG-001").',
    ],
    pasos: [
      { actor: SUPERVISOR, accion: 'Guardar las plantillas personalizadas.' },
      { actor: PACIENTE, accion: 'Provocar el rechazo por capacidad llena (llenar la cola) y verificar que el mensaje mostrado es EXACTAMENTE el personalizado.' },
      { actor: PACIENTE, accion: 'Provocar el rechazo "sin médicos" (deshabilitar a todos) y verificar el mensaje personalizado correspondiente.' },
      { actor: SUPERVISOR, accion: 'Borrar una de las plantillas personalizadas (dejarla vacía) y guardar.' },
      { actor: PACIENTE, accion: 'Provocar el mismo rechazo y verificar que vuelve a mostrar el texto POR DEFECTO.' },
    ],
    checkpoints: [
      'El paciente ve el texto personalizado exacto, incluyendo variables interpoladas si aplica (ej. {tiempo}).',
      'Al vaciar una plantilla, el sistema cae al texto por defecto automáticamente.',
    ],
  },

  // ───────────────────────────────────────────────────────────────────────
  // D. Configuración global, regiones, monitor y dashboard
  // ───────────────────────────────────────────────────────────────────────
  {
    id: 'ESC-20',
    area: 'Configuración global, regiones, monitor y dashboard',
    titulo: 'Configuración global (/admin/configuracion)',
    objetivo: 'Verificar que los parámetros de Configuración global se guardan y su efecto es visible donde corresponda.',
    configuracionInicial: ['Sesión iniciada como Supervisor ("Configuración" es EXCLUSIVA del Supervisor — `configuracion/{docId}` y `config/teleconsulta` en firestore.rules exigen `hasRole(\'supervisor\')`; el Administrador ya no tiene acceso).'],
    pasos: [
      { actor: SUPERVISOR, accion: 'Ir a Configuración global y revisar cada sección disponible.' },
      { actor: SUPERVISOR, accion: 'Cambiar un parámetro (ej. el flag de teleconsulta activa/inactiva) y guardar.' },
      { actor: SUPERVISOR, accion: 'Recargar la página y confirmar que el cambio persistió.' },
      { actor: PACIENTE, accion: 'Si el parámetro afecta a la app (ej. teleconsulta activa=false), verificar el efecto correspondiente en la app móvil.' },
    ],
    checkpoints: [
      'Los cambios se guardan sin error y persisten tras recargar.',
      'El efecto del parámetro se refleja en el lugar correcto (app móvil o portal, según el flag).',
    ],
  },
  {
    id: 'ESC-21',
    area: 'Configuración global, regiones, monitor y dashboard',
    titulo: 'Regiones de teleconsulta',
    objetivo: 'Verificar que el supervisor habilita regiones y que el paciente solo puede acceder a salas de su región habilitada.',
    configuracionInicial: [
      'Sesión iniciada como Supervisor (EXCLUSIVO — `config/regionesTeleconsulta` en firestore.rules exige `hasRole(\'supervisor\')`; el Administrador ya no tiene acceso).',
      'Al menos 2 regiones configuradas, una habilitada y otra deshabilitada.',
      'Una cuenta de paciente sin región asignada aún.',
    ],
    pasos: [
      { actor: SUPERVISOR, accion: 'Ir a Regiones Teleconsulta y confirmar cuáles están habilitadas.' },
      { actor: PACIENTE, accion: 'En el registro/perfil, elegir su región (debe listar solo las habilitadas).' },
      { actor: PACIENTE, accion: 'Intentar acceder a una sala de una región NO habilitada (si aplica un flujo directo) y verificar que no está disponible.' },
      { actor: SUPERVISOR, accion: 'Deshabilitar la región que el paciente eligió.' },
      { actor: PACIENTE, accion: 'Verificar el efecto: el paciente ya no puede solicitar cupo en salas de esa región (o se le pide elegir otra).' },
    ],
    checkpoints: [
      'El selector de región en la app solo muestra regiones habilitadas.',
      'La región queda guardada en el perfil del paciente (`users/{uid}.region`).',
      'Deshabilitar una región bloquea nuevas solicitudes asociadas a ella.',
    ],
  },
  {
    id: 'ESC-22',
    area: 'Configuración global, regiones, monitor y dashboard',
    titulo: 'Monitor en vivo',
    objetivo: 'Verificar que el Monitor en vivo refleja en tiempo real la cola, médicos y consultas en curso.',
    configuracionInicial: [
      'Sesión iniciada como Supervisor (es quien monitorea la operación en vivo día a día; el Administrador también tiene acceso, más orientado a supervisión estratégica).',
      'Al menos una sala activa con médicos y pacientes en cola.',
    ],
    pasos: [
      { actor: SUPERVISOR, accion: 'Abrir Monitor en vivo y ubicar la sala de prueba.' },
      { actor: PACIENTE, accion: 'Solicitar un cupo nuevo y verificar que aparece en el monitor casi de inmediato.' },
      { actor: MEDICO, accion: 'Pasar a "disponible"/"en pausa" y verificar que el monitor refleja el cambio de estado.' },
      { actor: MEDICO, accion: 'Tomar una consulta y verificar que el monitor la muestra "en atención".' },
    ],
    checkpoints: [
      'Los cambios de cola, estado del médico y consultas en curso se reflejan sin recargar manualmente la página.',
      'Los números (en espera, en atención) coinciden con la realidad de Firestore.',
    ],
  },
  {
    id: 'ESC-23',
    area: 'Configuración global, regiones, monitor y dashboard',
    titulo: 'Dashboard (global y por médico)',
    objetivo: 'Verificar que el Dashboard muestra indicadores globales para el staff administrativo y que el médico ve únicamente los suyos.',
    configuracionInicial: [
      'Sesión iniciada como Supervisor (uso operativo diario; la matriz de accesos ya confirma en ESC-10 que el Administrador también puede entrar).',
      'Historial de al menos algunas consultas finalizadas (de días previos o de esta sesión de pruebas).',
    ],
    pasos: [
      { actor: SUPERVISOR, accion: 'Abrir Dashboard y revisar "Capacidad del sistema" y "Estado de médicos" (conectado/pausa/desconectado).' },
      { actor: SUPERVISOR, accion: 'Verificar que las cifras de médicos en línea coinciden con lo que se ve en Monitor en vivo.' },
      { actor: MEDICO, accion: 'Iniciar sesión con una cuenta de médico e ir a su Dashboard.' },
      { actor: MEDICO, accion: 'Verificar que solo ve SUS indicadores (pacientes atendidos, no todos los de la sala/sistema).' },
    ],
    checkpoints: [
      '"Capacidad del sistema" y "Estado de médicos" muestran datos correctos y consistentes con Monitor en vivo.',
      'El dashboard del médico está filtrado a su propia actividad, no a la de todos.',
    ],
  },

  // ───────────────────────────────────────────────────────────────────────
  // E. App móvil — triage y admisión a la cola
  // ───────────────────────────────────────────────────────────────────────
  {
    id: 'ESC-24',
    area: 'App móvil — triage y admisión a la cola',
    titulo: 'Triage: el paciente SÍ aplica para teleconsulta',
    objetivo: 'Verificar que un triage con síntomas leves/moderados permite continuar hacia la cola.',
    configuracionInicial: ['Cuenta de paciente validada. Sala activa y con capacidad disponible.'],
    pasos: [
      { actor: PACIENTE, accion: 'Iniciar "Solicitar consulta" y completar el triage con síntomas leves (ej. resfriado común).' },
      { actor: SISTEMA, accion: 'Evaluar el triage y determinar que SÍ aplica para teleconsulta.' },
      { actor: PACIENTE, accion: 'Confirmar que puede continuar y entrar a la cola de la sala correspondiente.' },
    ],
    checkpoints: [
      'El triage no bloquea al paciente cuando los síntomas son compatibles con teleconsulta.',
      'El paciente entra a la cola con la prioridad/derivación correcta según el nivel del triage.',
    ],
  },
  {
    id: 'ESC-25',
    area: 'App móvil — triage y admisión a la cola',
    titulo: 'Triage: el paciente NO aplica para teleconsulta',
    objetivo: 'Verificar que un triage con nivel de urgencia alto (emergencia) NO deja entrar a la cola y orienta correctamente al paciente.',
    configuracionInicial: ['Cuenta de paciente validada.'],
    pasos: [
      { actor: PACIENTE, accion: 'Iniciar "Solicitar consulta" y completar el triage con síntomas de emergencia (ej. dolor de pecho intenso).' },
      { actor: SISTEMA, accion: 'Evaluar el triage y determinar que NO aplica para teleconsulta (requiere atención presencial/emergencia).' },
      { actor: PACIENTE, accion: 'Verificar que la app NO permite continuar a la cola y muestra la orientación correspondiente (ej. acudir a emergencias).' },
    ],
    checkpoints: [
      'El paciente es bloqueado ANTES de poder entrar a la cola cuando el triage indica emergencia.',
      'El mensaje de orientación es claro (dónde acudir / qué hacer).',
      'No queda ninguna consulta creada en estado "en_espera" para este intento.',
    ],
  },
  {
    id: 'ESC-26',
    area: 'App móvil — triage y admisión a la cola',
    titulo: 'Sin médicos en línea → rechazo; médico entra → admite',
    objetivo: 'Verificar que sin médicos disponibles se rechaza la entrada a la cola, y que apenas hay un médico en línea la solicitud es admitida.',
    configuracionInicial: [
      'Sala creada y habilitada (activa, dentro de horario).',
      'CERO médicos conectados/disponibles en la sala.',
    ],
    cambiosConfiguracion: [
      'Un médico se conecta y queda "disponible" en la sala a mitad de la prueba.',
    ],
    pasos: [
      { actor: PACIENTE, accion: 'Con 0 médicos en línea, solicitar entrar a la cola de la sala.' },
      { actor: SISTEMA, accion: 'Verificar el rechazo: "No hay médicos disponibles en esta sala".' },
      { actor: MEDICO, accion: 'Iniciar sesión y ponerse "disponible" en esa misma sala.' },
      { actor: PACIENTE, accion: 'Reintentar la solicitud de cupo.' },
      { actor: SISTEMA, accion: 'Verificar que ahora es ADMITIDO a la cola.' },
    ],
    checkpoints: [
      'Sin médicos, el rechazo ocurre de forma consistente (no admite "por accidente").',
      'Apenas un médico está disponible, el mismo paciente puede entrar sin cambios adicionales de configuración.',
    ],
    notas: 'Este es el escenario de referencia acordado con el cliente: combina configuración + verificación negativa + cambio de estado + verificación positiva en una sola ejecución.',
  },
  {
    id: 'ESC-27',
    area: 'App móvil — triage y admisión a la cola',
    titulo: 'Capacidad fija alcanzada → rechazo; liberar cupo → admite',
    objetivo: 'Verificar el rechazo al llenar la cola y la admisión al liberarse un cupo.',
    configuracionInicial: ['Sala con "Cupos fijos", tope máximo = 2, 1 médico disponible.'],
    pasos: [
      { actor: PACIENTE, accion: 'Paciente 1 y Paciente 2 solicitan cupo y son admitidos (cola llena = 2/2).' },
      { actor: PACIENTE, accion: 'Paciente 3 solicita cupo → verificar el rechazo por capacidad llena.' },
      { actor: PACIENTE, accion: 'Paciente 1 cancela su lugar en la cola (o es atendido y finaliza).' },
      { actor: PACIENTE, accion: 'Paciente 3 reintenta → verificar que ahora es admitido.' },
    ],
    checkpoints: [
      'La cola nunca supera el tope configurado.',
      'Liberar un cupo (cancelación o atención) permite admitir al siguiente en espera.',
    ],
  },
  {
    id: 'ESC-28',
    area: 'App móvil — triage y admisión a la cola',
    titulo: 'Sesión única (una consulta activa por paciente)',
    objetivo: 'Verificar que un paciente con una consulta activa no puede crear una segunda simultáneamente.',
    configuracionInicial: ['Paciente con una consulta ya en estado "en_espera" o "en_atencion".'],
    pasos: [
      { actor: PACIENTE, accion: 'Con una consulta activa, intentar solicitar un cupo nuevo (misma sala u otra).' },
      { actor: SISTEMA, accion: 'Verificar el rechazo: "Ya tiene una consulta activa".' },
      { actor: PACIENTE, accion: 'Finalizar o cancelar la consulta activa.' },
      { actor: PACIENTE, accion: 'Solicitar un cupo nuevo y verificar que ahora sí es admitido.' },
    ],
    checkpoints: [
      'Nunca se crean dos consultas activas simultáneas para el mismo paciente.',
      'Al liberar la consulta activa, el paciente puede solicitar una nueva sin fricción.',
    ],
  },
  {
    id: 'ESC-56',
    area: 'App móvil — triage y admisión a la cola',
    titulo: 'Supervisor cancela pacientes en espera por falta de médicos (selección múltiple + motivo)',
    objetivo:
      'Verificar que un supervisor/admin puede seleccionar uno o más pacientes en_espera en la cola y cancelarlos en bloque con un motivo obligatorio, que quedan fuera de la cola de inmediato, que el paciente recibe una notificación con el motivo, y que la consulta queda clasificada (no perdida) para poder consultarse luego en el historial.',
    configuracionInicial: [
      'Sesión iniciada como Supervisor (el Administrador también puede hacerlo).',
      'Una sala con al menos 3 pacientes en estado "en_espera" (usar cuentas de prueba distintas de la app móvil, con notificaciones push habilitadas en al menos una de ellas).',
    ],
    pasos: [
      { actor: SUPERVISOR, accion: 'En la Cola de Pacientes de la sala, marcar el checkbox de 2 de los 3 pacientes en espera.' },
      { actor: SUPERVISOR, accion: 'Verificar que aparece la barra "N pacientes seleccionados" con el botón "Cancelar seleccionados".' },
      { actor: SUPERVISOR, accion: 'Tocar "Cancelar seleccionados" y, en el modal, intentar confirmar SIN escribir motivo → verificar que el botón "Confirmar cancelación" permanece deshabilitado.' },
      { actor: SUPERVISOR, accion: 'Escribir un motivo (ej. "No hay médicos disponibles en este momento, intente de nuevo en 30 minutos") y confirmar.' },
      { actor: SISTEMA, accion: 'Verificar que ambos pacientes desaparecen de la cola de inmediato (en tiempo real, sin recargar) y que el contador "en espera" baja en 2.' },
      { actor: PACIENTE, accion: 'En el dispositivo con notificaciones habilitadas (app en segundo plano o cerrada), verificar que llega un push indicando que la teleconsulta fue cancelada, incluyendo el motivo escrito por el supervisor.' },
      { actor: PACIENTE, accion: 'Tocar la notificación y verificar que lleva a "Mis Teleconsultas" o al detalle correspondiente.' },
      { actor: SUPERVISOR, accion: 'Ir al Historial y ubicar una de las dos consultas canceladas.' },
      { actor: SUPERVISOR, accion: 'Abrir su detalle (ConsultaDetallePage) y verificar la disposición "Cancelada por supervisor" junto con el motivo exacto escrito.' },
      { actor: PACIENTE, accion: 'Desde la app móvil, abrir "Mis Teleconsultas" y verificar que la consulta cancelada aparece clasificada allí (no desaparece sin explicación) con su motivo.' },
      { actor: PACIENTE, accion: 'Verificar que el tercer paciente (NO seleccionado) sigue normalmente en la cola, sin ningún efecto.' },
    ],
    checkpoints: [
      'El checkbox de selección y la barra de acción masiva solo son visibles para Supervisor/Administrador (no para Médico).',
      'El motivo es obligatorio: no se puede confirmar la cancelación con el campo vacío.',
      'Los pacientes seleccionados salen de la cola de inmediato (tiempo real) y el contador de la sala se actualiza correctamente; el paciente NO seleccionado no se ve afectado.',
      'La notificación push recibida por el paciente incluye el motivo real escrito por el supervisor (no un mensaje genérico).',
      'La consulta cancelada queda con estado "finalizada" y disposición "cancelada_supervisor" — por eso es consultable en el Historial (portal) y en "Mis Teleconsultas" (móvil), a diferencia de una cancelación normal del propio paciente.',
      'El motivo se muestra tal cual tanto en el detalle del portal como en el detalle de la app móvil.',
    ],
    notas: 'Complementa a ESC-57, que verifica específicamente el aviso en tiempo real cuando el paciente está en la pantalla de espera SIN notificaciones push habilitadas. La selección y cancelación se hace en /salas, la única cola del portal (la vista por sala en /sala/:salaId se retiró y ahora redirige allí; cuando el usuario tiene varias salas, /salas filtra por sala con los chips de la cabecera).',
  },
  {
    id: 'ESC-57',
    area: 'App móvil — triage y admisión a la cola',
    titulo: 'Cancelación por supervisor con el paciente en la pantalla de espera (sin notificaciones push)',
    objetivo:
      'Verificar que, si el paciente está activamente en la pantalla de "espera de atención" de la app en el momento de la cancelación, recibe el aviso EN LA PANTALLA aunque tenga las notificaciones push deshabilitadas — cubriendo el caso donde el push por sí solo no llegaría.',
    configuracionInicial: [
      'Un paciente de prueba con las notificaciones push DESHABILITADAS (a nivel de sistema operativo o dentro de la app).',
      'Ese paciente solicita un cupo y permanece con la app abierta en la pantalla de espera ("Buscando médico disponible..." / posición en cola).',
      'Sesión de Supervisor en el portal, viendo la cola de esa misma sala.',
    ],
    pasos: [
      { actor: SUPERVISOR, accion: 'Con el paciente de prueba visible y esperando en la cola, seleccionarlo y cancelarlo con un motivo distintivo (ej. "PRUEBA-ESC57").' },
      { actor: PACIENTE, accion: 'Sin tocar ni recargar la app, verificar que la pantalla de espera muestra un aviso (alerta) de que la teleconsulta fue cancelada, incluyendo el motivo escrito.' },
      { actor: PACIENTE, accion: 'Confirmar el aviso y verificar que la app navega fuera de la pantalla de espera (no queda esperando indefinidamente a un médico que nunca llegará).' },
      { actor: PACIENTE, accion: 'Confirmar que NO llegó ningún push del sistema operativo (las notificaciones están deshabilitadas) — el aviso vino únicamente por estar viendo la pantalla en tiempo real.' },
    ],
    checkpoints: [
      'El aviso en pantalla aparece sin que el paciente recargue ni salga y vuelva a entrar a la app — es un listener en tiempo real sobre la consulta, no un mensaje que dependa del push.',
      'El motivo mostrado en pantalla coincide exactamente con el escrito por el supervisor.',
      'Tras confirmar el aviso, la app saca al paciente de la pantalla de espera (no lo deja "colgado" viendo una cola de la que ya fue removido).',
      'Este comportamiento es el respaldo real para pacientes sin notificaciones habilitadas — no depende de FCM/push en absoluto.',
    ],
    notas: 'Complementa a ESC-56 (flujo principal con push). Si se quiere cubrir el caso inverso (paciente CON push habilitado pero con la app cerrada/en segundo plano en el momento de la cancelación), ese camino ya lo cubre ESC-56.',
  },
  {
    id: 'ESC-52',
    area: 'App móvil — triage y admisión a la cola',
    titulo: 'Espera larga en cola con la app ABIERTA en primer plano',
    objetivo: 'Verificar que, con la app abierta y visible durante una espera larga, la posición en cola se sigue actualizando y el paciente pasa automáticamente a la videollamada cuando el médico lo toma, sin importar cuánto dure la espera.',
    configuracionInicial: [
      'Sala con capacidad limitada para poder generar una espera real (o coordinar con el médico para demorar la toma del paciente a propósito).',
      'Probar con distintos tiempos de espera antes de que el médico tome al paciente: ~15 min, ~30 min, ~60 min y 180+ min. Documentar en la causa cuál(es) se ejecutaron — lo ideal es cubrir varias corridas en tiempos distintos, ya que el comportamiento podría degradarse solo en esperas muy largas.',
    ],
    pasos: [
      { actor: PACIENTE, accion: 'Solicitar cupo y quedar en la pantalla de espera, con la app abierta y en primer plano todo el tiempo (sin bloquear el teléfono ni salir de la app).' },
      { actor: PACIENTE, accion: 'Durante la espera, verificar periódicamente que el número de posición en cola se sigue actualizando (se refresca cada pocos segundos).' },
      { actor: MEDICO, accion: 'Tras el tiempo de espera elegido, tomar/iniciar la consulta del paciente.' },
      { actor: PACIENTE, accion: 'Verificar que, sin ninguna interacción manual, la app transiciona automáticamente a la pantalla de "Aceptar llamada" en cuanto el médico inicia.' },
    ],
    checkpoints: [
      'La posición en cola se sigue actualizando durante toda la espera, sin congelarse (15, 30, 60 y 180+ min).',
      'Sin importar cuánto haya durado la espera, la app transiciona automáticamente a la pantalla de aceptar llamada apenas el médico la toma — no requiere que el paciente recargue ni reabra la app.',
      'No aparece ningún error de sesión/token expirado tras esperas largas.',
    ],
    notas: 'La pantalla de espera usa un listener de Firestore en tiempo real (`onSnapshot`) sobre el documento de la consulta más un polling de la posición cada ~8s; mientras la app siga en primer plano debería funcionar sin límite de tiempo, pero conviene confirmarlo empíricamente en 180+ min. Nota aparte: no existe ningún timeout en el backend que cancele automáticamente a un paciente que lleva mucho tiempo en "en_espera" — puede quedar esperando indefinidamente si ningún médico lo toma, lo cual también vale la pena documentar si se observa.',
  },
  {
    id: 'ESC-53',
    area: 'App móvil — triage y admisión a la cola',
    titulo: 'Espera larga en cola con la app abierta pero el TELÉFONO BLOQUEADO',
    objetivo: 'Documentar si, con el teléfono bloqueado (pantalla apagada) pero sin haber cerrado la app, el paciente se sigue enterando de su turno — por la notificación push o al desbloquear.',
    configuracionInicial: [
      'Mismo setup que ESC-52. Probar con distintos tiempos de espera: ~15 min, ~30 min, ~60 min y 180+ min (documentar en la causa cuál se ejecutó).',
    ],
    pasos: [
      { actor: PACIENTE, accion: 'Solicitar cupo, llegar a la pantalla de espera, y luego bloquear la pantalla del teléfono (botón de encendido) SIN cerrar la app — dejarla en segundo plano suspendido por el sistema operativo.' },
      { actor: PACIENTE, accion: 'Dejar el teléfono bloqueado durante el tiempo de espera elegido.' },
      { actor: MEDICO, accion: 'Tras el tiempo de espera, tomar/iniciar la consulta del paciente.' },
      { actor: PACIENTE, accion: 'Verificar si llega la notificación push "Videollamada entrante" a la pantalla de bloqueo.' },
      { actor: PACIENTE, accion: 'Desbloquear el teléfono y observar en qué pantalla queda la app: ¿ya está en la pantalla de aceptar llamada, o quedó congelada en la pantalla de espera?' },
      { actor: PACIENTE, accion: 'Si quedó congelada, tocar la notificación (o reabrir la app manualmente) y verificar que logra llegar a la videollamada sin perder el turno.' },
    ],
    checkpoints: [
      'ESPERADO (criterio a validar): la notificación push "Videollamada entrante" llega a la pantalla de bloqueo aunque el teléfono esté bloqueado (el backend la envía con prioridad alta/time-sensitive).',
      'ESPERADO (criterio a validar): al desbloquear o tocar la notificación, el paciente llega a la videollamada sin perder su turno.',
      'Si la pantalla de espera queda congelada al desbloquear (sin refrescar sola), documentarlo — es esperable dado que el listener en tiempo real no tiene reconexión automática al volver de segundo plano, pero es importante confirmar que al menos la notificación + reapertura manual resuelven el caso.',
      'Documentar el comportamiento REAL observado en la causa, para cada tiempo de espera probado — este escenario es de descubrimiento.',
    ],
    notas: 'La notificación existe en el backend (Cloud Function `notificarLlamadaEntrante`, dispara cuando el médico entra a la sala) y usa canal de prioridad máxima en Android e `interruptionLevel: time-sensitive` en iOS, así que debería atravesar el bloqueo de pantalla. Lo que hay que confirmar empíricamente es si, al reaccionar el paciente, la app lo lleva a la llamada sin fricción.',
  },
  {
    id: 'ESC-54',
    area: 'App móvil — triage y admisión a la cola',
    titulo: 'Espera larga en cola con la app CERRADA',
    objetivo: 'Documentar si, con la app completamente cerrada (no solo minimizada), el paciente se entera de su turno vía notificación push y puede unirse a la llamada sin perder su turno.',
    configuracionInicial: [
      'Mismo setup que ESC-52. Probar con distintos tiempos de espera: ~15 min, ~30 min, ~60 min y 180+ min (documentar en la causa cuál se ejecutó).',
    ],
    pasos: [
      { actor: PACIENTE, accion: 'Solicitar cupo, ver la pantalla de espera, y luego cerrar la app por completo (deslizarla fuera de la lista de apps recientes, no solo minimizarla).' },
      { actor: PACIENTE, accion: 'Dejar el teléfono así (con la app cerrada) durante el tiempo de espera elegido.' },
      { actor: MEDICO, accion: 'Tras el tiempo de espera, tomar/iniciar la consulta del paciente.' },
      { actor: PACIENTE, accion: 'Verificar si llega la notificación push "Videollamada entrante" pese a que la app estaba completamente cerrada.' },
      { actor: PACIENTE, accion: 'Tocar la notificación (o reabrir la app manualmente si no llegó ninguna) y verificar a qué pantalla lo lleva, y si logra unirse a la llamada sin perder el turno.' },
    ],
    checkpoints: [
      'ESPERADO (criterio a validar): la notificación push llega aunque la app esté completamente cerrada (FCM entrega notificaciones sin depender de que el proceso de la app siga vivo).',
      'ESPERADO (criterio a validar): tocar la notificación reabre la app y la lleva a la videollamada, o a un punto desde el cual el paciente puede unirse fácilmente, sin perder su turno.',
      'CRÍTICO: si NO llega ninguna notificación, o el paciente queda en el Home sin ninguna indicación de que lo llamaron, marcar como Fail — con la app cerrada, la notificación es la ÚNICA vía posible para enterarse (no hay ningún listener de Firestore activo).',
      'El médico no debería marcar al paciente como "no show" mientras este reacciona a la notificación dentro de una ventana razonable (ver nota sobre el timeout de 20 min).',
      'Documentar el comportamiento REAL observado en la causa, para cada tiempo de espera probado — este escenario es de descubrimiento.',
    ],
    notas: 'A diferencia de ESC-52/ESC-53, aquí no hay ningún listener de Firestore activo (el proceso de la app fue terminado), así que la notificación push es la única señal posible. Importante: el backend marca al paciente como "no show" si no se une dentro de 20 minutos desde que el médico entra a la sala (`NO_SHOW_TIMEOUT_MINS`) — con la app cerrada, el tiempo de reacción (ver notificación, desbloquear, abrir, unirse) consume parte de esos 20 minutos, así que vale la pena cronometrar cuánto tarda el paciente en unirse desde que llega la notificación.',
  },
  {
    id: 'ESC-55',
    area: 'App móvil — triage y admisión a la cola',
    titulo: 'Espera larga en cola con la app CERRADA y el teléfono BLOQUEADO',
    objetivo: 'Documentar el peor caso combinado: app completamente cerrada y teléfono bloqueado durante toda la espera.',
    configuracionInicial: [
      'Mismo setup que ESC-52. Probar con distintos tiempos de espera: ~15 min, ~30 min, ~60 min y 180+ min (documentar en la causa cuál se ejecutó).',
    ],
    pasos: [
      { actor: PACIENTE, accion: 'Solicitar cupo, ver la pantalla de espera, cerrar la app por completo Y bloquear la pantalla del teléfono.' },
      { actor: PACIENTE, accion: 'Dejar el teléfono así (cerrado y bloqueado) durante el tiempo de espera elegido.' },
      { actor: MEDICO, accion: 'Tras el tiempo de espera, tomar/iniciar la consulta del paciente.' },
      { actor: PACIENTE, accion: 'Verificar si la notificación "Videollamada entrante" despierta la pantalla de bloqueo (sonido/vibración/banner visible sin desbloquear).' },
      { actor: PACIENTE, accion: 'Cronometrar cuánto tarda en desbloquear, tocar la notificación y llegar a la videollamada desde que la notificación llega.' },
      { actor: PACIENTE, accion: 'Confirmar si logra unirse a la llamada dentro de la ventana de 20 minutos antes de que el sistema lo marque como "no show".' },
    ],
    checkpoints: [
      'ESPERADO (criterio a validar): la notificación despierta visiblemente la pantalla bloqueada (no pasa inadvertida).',
      'ESPERADO (criterio a validar): el paciente logra unirse a la videollamada dentro de la ventana de 20 minutos del timeout de "no show", incluso partiendo de teléfono bloqueado y app cerrada.',
      'CRÍTICO: si el paciente es marcado "no show" por el tiempo que toma reaccionar desde este peor caso, documentarlo como Fail — es el escenario más realista de "el paciente se quedó dormido/ocupado esperando" y el más importante de los cuatro para decidir si 20 minutos es un margen suficiente.',
      'Documentar el comportamiento REAL observado en la causa, para cada tiempo de espera probado — este escenario es de descubrimiento.',
    ],
    notas: 'Combina ESC-53 (teléfono bloqueado) + ESC-54 (app cerrada). Es el escenario más exigente de esta serie de espera larga; sus resultados son los más relevantes para decidir si el timeout de "no show" (`NO_SHOW_TIMEOUT_MINS=20`) necesita ampliarse para esperas largas.',
  },
  {
    id: 'ESC-58',
    area: 'App móvil — triage y admisión a la cola',
    titulo: 'Cancelación automática por límite de espera (cierre de la última sala + 1h)',
    objetivo:
      'Verificar que un paciente que queda "en_espera" sin ser tomado se cancela automáticamente pasado el cierre de la ÚLTIMA sala activa del día más 1 hora de margen, con un motivo genérico, y que una sala que cierra ANTES no dispara la cancelación por sí sola mientras otra sala siga activa.',
    configuracionInicial: [
      'Dos salas activas hoy con horas de cierre distintas: Sala A cierra primero (ej. en los próximos minutos), Sala B cierra más tarde (ej. 15-20 min después que la Sala A) — para poder confirmar que el límite usado es el de la sala que cierra MÁS TARDE, no el de la primera en cerrar.',
      'En la Sala A, un paciente de prueba solicita cupo y queda "en_espera" SIN que ningún médico lo tome (coordinar para que los médicos de esa sala no lo tomen, o dejarla sin médicos disponibles).',
      'Anotar la hora de cierre efectiva de la Sala B (horaFin + su bufferCierreMins) — el límite del sistema será esa hora + 1 hora adicional.',
    ],
    pasos: [
      { actor: PACIENTE, accion: 'Solicitar cupo en la Sala A y quedar en la pantalla de espera, sin que ningún médico lo tome.' },
      { actor: SUPERVISOR, accion: 'Una vez que la Sala A (la que cierra primero) ya cerró, verificar en Monitor en vivo que el paciente sigue "en_espera" — no debe cancelarse solo porque SU sala cerró.' },
      { actor: PACIENTE, accion: 'Esperar hasta pasar el límite global: hora de cierre efectiva de la Sala B (la que cierra más tarde) + 1 hora (el cron corre cada 5 min, así que puede haber unos minutos de margen adicional).' },
      { actor: SUPERVISOR, accion: 'Verificar en Auditoría que aparece el evento "CONSULTAS_CANCELADAS_LIMITE_ESPERA" una vez cumplido el límite.' },
      { actor: PACIENTE, accion: 'Si tiene la app abierta, verificar que recibe de inmediato el aviso "Su teleconsulta fue cancelada" con el motivo genérico.' },
      { actor: PACIENTE, accion: 'Si tiene la app cerrada, verificar que recibe la notificación push equivalente.' },
      { actor: SUPERVISOR, accion: 'En el Historial, abrir el detalle de la consulta cancelada y confirmar la disposición y el motivo.' },
    ],
    checkpoints: [
      'El paciente en la Sala A (que cerró primero) NO se cancela solo porque su propia sala cerró — sigue "en_espera" mientras la Sala B (la última en cerrar) sigue activa.',
      'La cancelación ocurre solo después de: cierre efectivo de la ÚLTIMA sala activa del día + 1 hora de margen (no antes).',
      'El motivo mostrado al paciente es genérico y no culpa a un médico ni expone detalles técnicos (algo como "Hubo demasiadas solicitudes de atención... intente nuevamente en la siguiente ventana de atención").',
      'La consulta queda con estado "finalizada" y disposición "cancelada_supervisor" (mismo patrón que ESC-56/ESC-57), visible en el historial del paciente igual que cualquier cierre normal.',
      'El aviso llega tanto en tiempo real (app abierta, igual que ESC-57) como por push (app cerrada, igual que ESC-56), sin necesidad de cambios adicionales en la app móvil.',
    ],
    notas: 'El cron `cancelarEsperaTrasCierre` corre cada 5 minutos, así que puede haber hasta ~5 min de retraso entre que se cumple el límite exacto y la cancelación real — es esperado, no un defecto. Reusa intencionalmente el mismo `estado`/`disposicion` que la cancelación manual del supervisor (ESC-56/ESC-57) para que la app móvil y el push ya existentes lo manejen sin cambios; se distingue internamente con el campo `motivoCierreSistema: "limite_espera_cola"` para diferenciarlo en auditoría de una cancelación manual real. Complementa a ESC-52/53/54/55 (espera larga bajo distintos estados del teléfono): aquellos verifican qué ve el paciente MIENTRAS espera; este verifica qué pasa cuando la espera se agota sin que nadie lo haya atendido.',
  },

  // ───────────────────────────────────────────────────────────────────────
  // F. Ciclo de atención (médico ↔ paciente)
  // ───────────────────────────────────────────────────────────────────────
  {
    id: 'ESC-29',
    area: 'Ciclo de atención (médico ↔ paciente)',
    titulo: 'Toma de consulta en modo Híbrido con oferta (aceptar y rechazar)',
    objetivo: 'Verificar que el médico recibe la oferta de un paciente, puede aceptarla, y que al rechazarla se reasigna a otro médico.',
    configuracionInicial: [
      'Sala en modo "Híbrido" con "Oferta antes de asignar" activada.',
      '2 médicos disponibles.',
    ],
    pasos: [
      { actor: PACIENTE, accion: 'Solicitar un cupo y quedar en espera.' },
      { actor: MEDICO, accion: 'El médico A recibe la oferta del paciente (aceptar/rechazar).' },
      { actor: MEDICO, accion: 'El médico A RECHAZA la oferta.' },
      { actor: SISTEMA, accion: 'Verificar que la oferta se reasigna al médico B.' },
      { actor: MEDICO, accion: 'El médico B ACEPTA la oferta.' },
      { actor: PACIENTE, accion: 'Verificar que pasa a estado "asignada"/"en_atencion" con el médico B.' },
    ],
    checkpoints: [
      'La oferta llega al médico correcto y con un tiempo de expiración razonable.',
      'Rechazar reasigna automáticamente a otro médico disponible (no se pierde el paciente).',
      'Aceptar vincula correctamente la consulta al médico que aceptó.',
    ],
  },
  {
    id: 'ESC-30',
    area: 'Ciclo de atención (médico ↔ paciente)',
    titulo: 'Aceptar llamada en el móvil (pantalla de aceptar)',
    objetivo: 'Verificar que, al iniciar el médico, el paciente ve la pantalla de aceptar con preview de cámara antes de entrar a la videollamada.',
    configuracionInicial: ['Consulta asignada a un médico, paciente esperando ser llamado.'],
    pasos: [
      { actor: MEDICO, accion: 'Iniciar la videollamada desde el portal.' },
      { actor: PACIENTE, accion: 'Verificar que aparece la pantalla de "Aceptar llamada" con preview de su cámara y toggle de video/audio.' },
      { actor: PACIENTE, accion: 'Aceptar la llamada.' },
      { actor: PACIENTE, accion: 'Verificar que entra correctamente a la pantalla de videollamada.' },
    ],
    checkpoints: [
      'La pantalla de aceptar aparece siempre que el médico inicia, sin excepciones.',
      'El preview de cámara funciona y el toggle de cámara/mic responde antes de aceptar.',
      'Tras aceptar, la transición a la videollamada es fluida (sin pantallas en negro ni cuelgues).',
    ],
  },
  {
    id: 'ESC-31',
    area: 'Ciclo de atención (médico ↔ paciente)',
    titulo: 'Videollamada: audio, video y fondo institucional',
    objetivo: 'Verificar la calidad básica de la videollamada y el fondo virtual del médico.',
    configuracionInicial: ['Consulta en curso entre médico y paciente, ambos con cámara/mic disponibles.'],
    pasos: [
      { actor: MEDICO, accion: 'Verificar que el video y audio del paciente se reciben correctamente.' },
      { actor: PACIENTE, accion: 'Verificar que el video y audio del médico se reciben correctamente, incluido el fondo institucional MINSA.' },
      { actor: MEDICO, accion: 'Alternar cámara y micrófono (encender/apagar) y confirmar que el paciente ve el cambio.' },
      { actor: PACIENTE, accion: 'Alternar su cámara y micrófono y confirmar que el médico ve el cambio.' },
    ],
    checkpoints: [
      'Audio y video son bidireccionales y sin cortes prolongados.',
      'El fondo institucional del médico se ve correctamente (no aparece "Sin fondo" salvo fallback esperado).',
      'Los toggles de cámara/mic funcionan en ambos sentidos.',
    ],
  },
  {
    id: 'ESC-32',
    area: 'Ciclo de atención (médico ↔ paciente)',
    titulo: 'Supervisor se une a la teleconsulta',
    objetivo: 'Verificar que un supervisor puede unirse a una consulta en curso (visible u oculto) y que el médico es notificado.',
    configuracionInicial: ['Consulta en curso entre médico y paciente. Cuenta de Supervisor disponible.'],
    pasos: [
      { actor: SUPERVISOR, accion: 'Ir a Supervisión y unirse a la consulta en curso.' },
      { actor: MEDICO, accion: 'Verificar que recibe una notificación/indicador de que el supervisor se unió.' },
      { actor: SUPERVISOR, accion: 'Confirmar que puede ver/escuchar la consulta según el modo configurado (visible/oculto).' },
      { actor: SUPERVISOR, accion: 'Salir de la supervisión.' },
    ],
    checkpoints: [
      'El médico es notificado cuando el supervisor se une (no es una observación silenciosa sin aviso).',
      'La consulta del paciente no se ve interrumpida por la presencia del supervisor.',
    ],
  },
  {
    id: 'ESC-33',
    area: 'Ciclo de atención (médico ↔ paciente)',
    titulo: 'Grabación, transcripción, SOAPE y asistente médico',
    objetivo: 'Verificar el consentimiento y arranque de grabación, la transcripción en vivo, el SOAPE generado y el asistente clínico.',
    configuracionInicial: ['Consulta en curso, secrets/bucket de LiveKit Egress configurados en el entorno.'],
    pasos: [
      { actor: MEDICO, accion: 'Aceptar el consentimiento HIPAA al iniciar la consulta.' },
      { actor: SISTEMA, accion: 'Verificar que la grabación se inicia (indicador visible) sin errores.' },
      { actor: MEDICO, accion: 'Hablar con el paciente y verificar que la transcripción aparece en vivo.' },
      { actor: MEDICO, accion: 'Revisar el SOAPE generado y confirmar que el texto es legible (sin JSON crudo en ninguna sección).' },
      { actor: MEDICO, accion: 'Usar el asistente médico IA para una duda clínica y verificar una respuesta con contexto de la consulta.' },
      { actor: SISTEMA, accion: 'Al finalizar, verificar que el archivo de grabación queda accesible desde el detalle de la consulta.' },
    ],
    checkpoints: [
      'La grabación arranca sin error tras el consentimiento (revisar logs si falla).',
      'La transcripción distingue correctamente médico vs. paciente.',
      'El SOAPE se muestra formateado, no como JSON crudo.',
      'La URL/reproducción de la grabación funciona después de finalizar.',
    ],
  },
  {
    id: 'ESC-46',
    area: 'Ciclo de atención (médico ↔ paciente)',
    titulo: 'Mucho ruido de fondo del lado del PACIENTE',
    objetivo: 'Verificar que la consulta se sostiene y que la transcripción/SOAPE no se degradan de forma silenciosa cuando el paciente tiene mucho ruido de fondo.',
    configuracionInicial: [
      'Consulta de video en curso.',
      'Simular ruido de fondo del lado del paciente: reproducir un audio de ruido (tráfico, cafetería, TV a volumen alto) desde otro dispositivo colocado cerca del micrófono del teléfono del paciente.',
    ],
    pasos: [
      { actor: PACIENTE, accion: 'Con el ruido de fondo sonando, conversar con el médico en tono normal (sin gritar para compensar).' },
      { actor: MEDICO, accion: 'Evaluar si logra entender al paciente pese al ruido (¿el audio es comprensible o se pierde por completo?).' },
      { actor: MEDICO, accion: 'Revisar la transcripción en vivo: ¿distingue lo que dijo el paciente del ruido de fondo, o se llena de texto sin sentido?' },
      { actor: MEDICO, accion: 'Al finalizar, revisar el SOAPE generado y confirmar que no incluye contenido erróneo o inventado atribuible al ruido.' },
    ],
    checkpoints: [
      'ESPERADO (criterio a validar): el médico puede seguir la conversación con el paciente pese al ruido, con una degradación razonable.',
      'ESPERADO (criterio a validar): la transcripción no atribuye al paciente frases que en realidad son solo ruido de fondo.',
      'El SOAPE generado no contiene alucinaciones o datos clínicos erróneos causados por el ruido.',
      'Documentar el comportamiento REAL observado en la causa — este escenario es de descubrimiento.',
    ],
    notas: 'Complementa a ESC-33 (transcripción/SOAPE en condiciones normales de audio).',
  },
  {
    id: 'ESC-47',
    area: 'Ciclo de atención (médico ↔ paciente)',
    titulo: 'Mucho ruido de fondo del lado del MÉDICO',
    objetivo: 'Verificar que la consulta se sostiene y que la transcripción/SOAPE no se degradan de forma silenciosa cuando el médico tiene mucho ruido de fondo.',
    configuracionInicial: [
      'Consulta de video en curso.',
      'Simular ruido de fondo del lado del médico: reproducir un audio de ruido desde otro dispositivo cerca del micrófono de la computadora, o si está disponible, mezclar una pista de ruido con el micrófono usando una entrada de audio virtual (p. ej. BlackHole/VB-Audio Cable) seleccionada como input en el navegador.',
    ],
    pasos: [
      { actor: MEDICO, accion: 'Con el ruido de fondo sonando, conversar con el paciente en tono normal.' },
      { actor: PACIENTE, accion: 'Evaluar si logra entender al médico pese al ruido (¿el audio es comprensible o se pierde por completo?).' },
      { actor: MEDICO, accion: 'Revisar la transcripción en vivo: ¿distingue lo que dijo el médico del ruido de fondo, o se llena de texto sin sentido?' },
      { actor: MEDICO, accion: 'Al finalizar, revisar el SOAPE generado y confirmar que no incluye contenido erróneo o inventado atribuible al ruido.' },
    ],
    checkpoints: [
      'ESPERADO (criterio a validar): el paciente puede seguir la conversación con el médico pese al ruido, con una degradación razonable.',
      'ESPERADO (criterio a validar): la transcripción no atribuye al médico frases que en realidad son solo ruido de fondo.',
      'El SOAPE generado no contiene alucinaciones o datos clínicos erróneos causados por el ruido.',
      'Documentar el comportamiento REAL observado en la causa — este escenario es de descubrimiento.',
    ],
    notas: 'Complementa a ESC-33 (transcripción/SOAPE en condiciones normales de audio).',
  },
  {
    id: 'ESC-48',
    area: 'Ciclo de atención (médico ↔ paciente)',
    titulo: 'Mucho ruido de fondo en AMBOS extremos',
    objetivo: 'Documentar el peor caso: médico y paciente con mucho ruido de fondo simultáneamente, y verificar que la consulta se pueda completar de forma mínima.',
    configuracionInicial: [
      'Consulta de video en curso.',
      'Aplicar simultáneamente el ruido del médico (ESC-47) y el del paciente (ESC-46).',
    ],
    pasos: [
      { actor: MEDICO, accion: 'Activar el ruido de fondo de su lado.' },
      { actor: PACIENTE, accion: 'Activar, en paralelo, el ruido de fondo de su lado.' },
      { actor: MEDICO, accion: 'Intentar mantener un intercambio verbal mínimo con el paciente y observar si la conversación sigue siendo posible.' },
      { actor: MEDICO, accion: 'Revisar la transcripción y el SOAPE resultantes: ¿son inservibles (puro ruido) o al menos rescatan lo esencial dicho por cada parte?' },
      { actor: MEDICO, accion: 'Intentar finalizar la consulta y registrar una disposición.' },
    ],
    checkpoints: [
      'ESPERADO (criterio a validar): la conversación sigue siendo posible aunque con calidad reducida (no se corta la llamada por el ruido).',
      'El médico puede finalizar la consulta y registrar la disposición aún en estas condiciones.',
      'Documentar el comportamiento REAL observado en la causa — este escenario es de descubrimiento y probablemente revele si se necesita alguna reducción de ruido (noise suppression) más agresiva.',
    ],
    notas: 'Combina ESC-46 + ESC-47.',
  },
  {
    id: 'ESC-49',
    area: 'Ciclo de atención (médico ↔ paciente)',
    titulo: 'Micrófono del PACIENTE bajo o alejado',
    objetivo: 'Verificar qué ocurre cuando el audio del paciente llega muy bajo (volumen de entrada bajo o teléfono alejado de quien habla), sin ser un corte de red ni ruido de fondo.',
    configuracionInicial: [
      'Consulta de video en curso.',
      'Simular audio bajo del paciente: bajar el volumen de entrada del micrófono del teléfono al mínimo (sin llegar a silenciarlo) en los ajustes del sistema, o colocar el teléfono a más de 1 metro de distancia de quien habla.',
    ],
    pasos: [
      { actor: PACIENTE, accion: 'Con el micrófono bajo/alejado, hablar con el médico en tono de voz normal (sin compensar gritando).' },
      { actor: MEDICO, accion: 'Evaluar si el audio del paciente se escucha, aunque sea bajo, o si es prácticamente inaudible.' },
      { actor: MEDICO, accion: 'Verificar si hay algún indicador visual de "audio bajo" del participante, o si el sistema no da ninguna señal.' },
      { actor: MEDICO, accion: 'Revisar la transcripción correspondiente a ese tramo: ¿queda vacía/incompleta, o logra capturar algo?' },
      { actor: PACIENTE, accion: 'Regresar el micrófono/distancia a condiciones normales y confirmar que el audio se recupera.' },
    ],
    checkpoints: [
      'ESPERADO (criterio a validar): el médico recibe alguna señal (visual o de calidad de audio) de que el paciente se escucha bajo, no un silencio ambiguo indistinguible de una desconexión.',
      'La transcripción no falla silenciosamente por completo durante ese tramo (o al menos el sistema no se rompe).',
      'Documentar el comportamiento REAL observado en la causa — este escenario es de descubrimiento.',
    ],
    notas: 'Distinto de ESC-44 (conexión lenta): aquí la red está bien, el problema es puramente de nivel/posición del micrófono.',
  },
  {
    id: 'ESC-50',
    area: 'Ciclo de atención (médico ↔ paciente)',
    titulo: 'Micrófono del MÉDICO bajo o alejado',
    objetivo: 'Verificar qué ocurre cuando el audio del médico llega muy bajo (volumen de entrada bajo o alejado del micrófono de la computadora), sin ser un corte de red ni ruido de fondo.',
    configuracionInicial: [
      'Consulta de video en curso.',
      'Simular audio bajo del médico: bajar el volumen de entrada del micrófono en los ajustes de sonido del sistema operativo (o del propio navegador si lo permite), o alejarse más de 1 metro del micrófono de la computadora.',
    ],
    pasos: [
      { actor: MEDICO, accion: 'Con el micrófono bajo/alejado, hablar con el paciente en tono de voz normal.' },
      { actor: PACIENTE, accion: 'Evaluar si el audio del médico se escucha, aunque sea bajo, o si es prácticamente inaudible.' },
      { actor: MEDICO, accion: 'Verificar si el portal muestra algún indicador de nivel de audio bajo propio.' },
      { actor: MEDICO, accion: 'Revisar la transcripción correspondiente a ese tramo: ¿queda vacía/incompleta, o logra capturar algo?' },
      { actor: MEDICO, accion: 'Regresar el micrófono/distancia a condiciones normales y confirmar que el audio se recupera.' },
    ],
    checkpoints: [
      'ESPERADO (criterio a validar): el paciente recibe alguna señal de que el médico se escucha bajo, no un silencio ambiguo.',
      'La transcripción no falla silenciosamente por completo durante ese tramo.',
      'Documentar el comportamiento REAL observado en la causa — este escenario es de descubrimiento.',
    ],
    notas: 'Distinto de ESC-43 (conexión lenta): aquí la red está bien, el problema es puramente de nivel/posición del micrófono.',
  },
  {
    id: 'ESC-51',
    area: 'Ciclo de atención (médico ↔ paciente)',
    titulo: 'Micrófono bajo o alejado en AMBOS extremos',
    objetivo: 'Documentar el peor caso: médico y paciente con audio bajo/alejado simultáneamente, y verificar si la consulta sigue siendo mínimamente viable.',
    configuracionInicial: [
      'Consulta de video en curso.',
      'Aplicar simultáneamente el micrófono bajo/alejado del médico (ESC-50) y del paciente (ESC-49).',
    ],
    pasos: [
      { actor: MEDICO, accion: 'Bajar/alejar su propio micrófono.' },
      { actor: PACIENTE, accion: 'Bajar/alejar, en paralelo, su propio micrófono.' },
      { actor: MEDICO, accion: 'Intentar mantener un intercambio verbal mínimo y observar si ambos se siguen escuchando, aunque sea con esfuerzo.' },
      { actor: MEDICO, accion: 'Revisar si la transcripción de ese tramo queda prácticamente vacía para ambos lados.' },
      { actor: MEDICO, accion: 'Intentar finalizar la consulta y registrar una disposición.' },
    ],
    checkpoints: [
      'ESPERADO (criterio a validar): la conversación sigue siendo posible, aunque con esfuerzo, sin que el sistema falle por completo.',
      'El médico puede finalizar la consulta y registrar la disposición aún en estas condiciones.',
      'Documentar el comportamiento REAL observado en la causa — este escenario es de descubrimiento.',
    ],
    notas: 'Combina ESC-49 + ESC-50.',
  },

  // ───────────────────────────────────────────────────────────────────────
  // G. Cuelgue y desconexión
  // ───────────────────────────────────────────────────────────────────────
  {
    id: 'ESC-34',
    area: 'Cuelgue y desconexión',
    titulo: 'El paciente cuelga la llamada',
    objetivo: 'Documentar el comportamiento real cuando el PACIENTE cuelga voluntariamente, y contrastarlo contra el esperado.',
    configuracionInicial: ['Consulta de video en curso entre médico y paciente.'],
    pasos: [
      { actor: PACIENTE, accion: 'Colgar la llamada desde la app (botón de finalizar/salir).' },
      { actor: MEDICO, accion: 'Observar qué ve el médico en el portal (¿se le indica que el paciente salió? ¿puede seguir en la sala?).' },
      { actor: PACIENTE, accion: 'Observar la pantalla del paciente tras colgar (¿vuelve a Home limpiamente? ¿pantalla negra?).' },
      { actor: MEDICO, accion: 'Finalizar/registrar disposición de la consulta desde el portal.' },
    ],
    checkpoints: [
      'ESPERADO: el médico ve claramente que el paciente salió y puede finalizar/registrar disposición sin quedar bloqueado.',
      'ESPERADO: el paciente vuelve a Home sin pantalla negra ni necesidad de forzar el cierre de la app.',
      'ESPERADO: el estado de la consulta queda coherente (no queda "en_atencion" indefinidamente).',
      'Registrar en la causa cualquier diferencia entre lo observado y lo esperado.',
    ],
  },
  {
    id: 'ESC-35',
    area: 'Cuelgue y desconexión',
    titulo: 'El médico cuelga/finaliza la llamada',
    objetivo: 'Documentar el comportamiento cuando el MÉDICO cuelga/finaliza, incluyendo el flujo de 2 pasos (Colgar/Finalizar).',
    configuracionInicial: ['Consulta de video en curso entre médico y paciente.'],
    pasos: [
      { actor: MEDICO, accion: 'Pulsar "Colgar" (primer paso) y verificar que NO finaliza la consulta todavía (según el diseño de 2 pasos).' },
      { actor: PACIENTE, accion: 'Observar qué ve el paciente inmediatamente después de que el médico cuelga.' },
      { actor: MEDICO, accion: 'Pulsar "Finalizar" (segundo paso) y registrar la disposición de la consulta.' },
      { actor: PACIENTE, accion: 'Verificar que el paciente sale correctamente de la llamada y ve el resultado/resumen.' },
    ],
    checkpoints: [
      'ESPERADO: "Colgar" no destruye la consulta; "Finalizar" sí, con disposición registrada.',
      'ESPERADO: el paciente sale de la videollamada de forma limpia (sin pantalla negra) apenas el médico finaliza.',
      'Registrar en la causa cualquier diferencia entre lo observado y lo esperado.',
    ],
  },
  {
    id: 'ESC-36',
    area: 'Cuelgue y desconexión',
    titulo: 'Se cae la conexión del PACIENTE (no cuelga voluntariamente)',
    objetivo: 'Documentar qué ocurre realmente cuando el paciente pierde conexión a internet a mitad de la consulta, y contrastarlo contra el comportamiento esperado.',
    configuracionInicial: ['Consulta de video en curso. Forma de simular: modo avión / desconectar WiFi en el dispositivo del paciente.'],
    pasos: [
      { actor: PACIENTE, accion: 'Activar modo avión (o cortar la red) a mitad de la consulta.' },
      { actor: MEDICO, accion: 'Observar qué ve el médico (¿aviso de "paciente desconectado"? ¿la pantalla se congela sin indicación?).' },
      { actor: PACIENTE, accion: 'Esperar ~1 minuto y restaurar la conexión.' },
      { actor: PACIENTE, accion: 'Observar si la app permite RECONECTAR a la misma consulta o si quedó fuera.' },
      { actor: MEDICO, accion: 'Si no hay reconexión en un tiempo razonable, observar qué opción tiene el médico (esperar, finalizar, marcar "no contestó/falla técnica").' },
    ],
    checkpoints: [
      'ESPERADO (criterio a validar): el médico recibe un aviso claro de desconexión del paciente (no un silencio ambiguo).',
      'ESPERADO (criterio a validar): si el paciente reconecta dentro de una ventana razonable, puede reincorporarse a LA MISMA consulta.',
      'ESPERADO (criterio a validar): si no reconecta, la consulta se cierra con una disposición coherente (ej. "falla técnica"), no queda huérfana.',
      'Documentar el comportamiento REAL observado en el campo de causa, sea o no el esperado — este escenario es de descubrimiento.',
    ],
    notas: 'Este escenario valida un caso donde el comportamiento actual puede no estar definido/probado. Si el resultado real difiere del esperado, se marca como Fail y se detalla para priorizar una mejora.',
  },
  {
    id: 'ESC-37',
    area: 'Cuelgue y desconexión',
    titulo: 'Se cae la conexión del MÉDICO (no cuelga voluntariamente)',
    objetivo: 'Documentar qué ocurre realmente cuando el médico pierde conexión a mitad de la consulta, y contrastarlo contra el comportamiento esperado.',
    configuracionInicial: ['Consulta de video en curso. Forma de simular: modo avión / desconectar WiFi en el dispositivo/PC del médico.'],
    pasos: [
      { actor: MEDICO, accion: 'Activar modo avión (o cortar la red) a mitad de la consulta.' },
      { actor: PACIENTE, accion: 'Observar qué ve el paciente (¿pantalla de "reconectando"? ¿se queda congelada sin aviso?).' },
      { actor: MEDICO, accion: 'Esperar ~1 minuto y restaurar la conexión.' },
      { actor: MEDICO, accion: 'Observar si el médico puede RETOMAR la misma consulta o si quedó cerrada/perdida.' },
      { actor: PACIENTE, accion: 'Si el médico no reconecta, observar qué le pasa al paciente (¿queda esperando indefinidamente? ¿se le informa? ¿se reencola?).' },
    ],
    checkpoints: [
      'ESPERADO (criterio a validar): el paciente recibe un aviso claro de que el médico se desconectó (no queda "colgado" sin explicación).',
      'ESPERADO (criterio a validar): si el médico reconecta a tiempo, puede retomar LA MISMA consulta sin perder el progreso (notas, SOAPE parcial).',
      'ESPERADO (criterio a validar): si no reconecta, el paciente tiene una salida razonable (reencolar, reprogramar, o aviso claro de qué sigue).',
      'Documentar el comportamiento REAL en el campo de causa — este escenario es de descubrimiento.',
    ],
    notas: 'Igual que ESC-36, prioriza documentar realidad vs. expectativa para decidir si se necesita una mejora (reconexión automática, timeout con reencolado, etc.).',
  },
  {
    id: 'ESC-43',
    area: 'Cuelgue y desconexión',
    titulo: 'Conexión lenta del MÉDICO (paciente en red normal)',
    objetivo: 'Documentar cómo se comporta la llamada cuando solo el médico tiene una conexión degradada (no caída), y si el sistema degrada de forma controlada en vez de cortar la consulta.',
    configuracionInicial: [
      'Consulta de video en curso. Paciente en red normal (WiFi/datos sin restricción).',
      'Simular red lenta del médico desde el navegador: DevTools (F12) → pestaña "Network" → menú de throttling (por defecto "No throttling") → seleccionar "Slow 3G" (o crear un perfil personalizado en "Network conditions" con ~400 Kbps de bajada/subida y ~400ms de latencia).',
    ],
    pasos: [
      { actor: MEDICO, accion: 'Con la consulta en curso, activar el throttling "Slow 3G" en DevTools.' },
      { actor: MEDICO, accion: 'Observar su propia experiencia: ¿el video/audio del paciente se ve entrecortado, congelado, o aparece algún indicador de conexión débil?' },
      { actor: PACIENTE, accion: 'Observar qué ve del lado del médico (¿recibe algún aviso de que el médico tiene mala conexión, o solo ve el video congelado sin explicación?).' },
      { actor: MEDICO, accion: 'Mientras dura el throttling, intentar seguir documentando el SOAPE y verificar que escribir/guardar notas no se bloquea por la lentitud del video.' },
      { actor: MEDICO, accion: 'Quitar el throttling (volver a "No throttling") y verificar que la llamada se recupera sin necesidad de reiniciarla.' },
    ],
    checkpoints: [
      'ESPERADO (criterio a validar): el paciente recibe alguna señal de que la conexión del médico está degradada, no un video congelado sin explicación.',
      'ESPERADO (criterio a validar): LiveKit prioriza audio sobre video o reduce automáticamente la calidad en vez de cortar la llamada.',
      'La documentación del médico (SOAPE) sigue funcionando con normalidad, independiente de la calidad del video.',
      'Al restaurar la conexión, la llamada se recupera sola (sin recargar la página ni reiniciar la sala).',
      'Documentar el comportamiento REAL observado en la causa, sea o no el esperado — este escenario es de descubrimiento.',
    ],
    notas: 'Complementa a ESC-37 (caída total de la conexión del médico): aquí la red no se corta, solo se degrada.',
  },
  {
    id: 'ESC-44',
    area: 'Cuelgue y desconexión',
    titulo: 'Conexión lenta del PACIENTE (médico en red normal)',
    objetivo: 'Documentar cómo se comporta la llamada cuando solo el paciente tiene una conexión degradada (no caída), del lado de la app móvil.',
    configuracionInicial: [
      'Consulta de video en curso. Médico en red normal (portal sin restricción).',
      'Simular red lenta en el dispositivo del paciente con UNA de estas opciones (documentar en la causa cuál se usó):',
      '  a) Emulador Android (Android Studio AVD): Extended Controls → Cellular → Network type "3G"/"HSPA" y Signal strength "Poor", o Extended Controls → Network Speed/Latency con un perfil bajo.',
      '  b) iOS con perfil de desarrollador: Ajustes → Developer → Network Link Conditioner, perfil "3G" o "Very Bad Network".',
      '  c) Dispositivo físico (iOS o Android): conectarlo a un hotspot WiFi compartido desde una laptop cuyo enlace de subida esté limitado (Mac: Network Link Conditioner sobre la interfaz de salida; Windows: NetLimiter/Clumsy; Linux: `tc`).',
      '  d) Alternativa de campo sin herramientas: activar un límite de ancho de banda (QoS) para la MAC del teléfono de prueba en el router, o ubicarse en una zona con señal WiFi/celular débil.',
    ],
    pasos: [
      { actor: PACIENTE, accion: 'Con la consulta en curso, activar la simulación de red lenta elegida.' },
      { actor: PACIENTE, accion: 'Observar su propia experiencia: ¿el video/audio del médico se ve entrecortado, o la app muestra algún indicador de "reconectando"/conexión débil?' },
      { actor: MEDICO, accion: 'Observar qué ve en el portal (¿recibe aviso de que el paciente tiene mala conexión, o solo ve la imagen congelada sin explicación?).' },
      { actor: PACIENTE, accion: 'Intentar interactuar (hablar, apagar/encender la cámara) y verificar que la app no se congela ni se cierra sola.' },
      { actor: PACIENTE, accion: 'Restaurar la red normal y verificar que la app se recupera sin necesidad de salir de la llamada.' },
    ],
    checkpoints: [
      'ESPERADO (criterio a validar): el médico recibe alguna señal de que la conexión del paciente está degradada, no una imagen congelada sin explicación.',
      'La app del paciente sigue respondiendo (no se congela ni cierra) durante la degradación.',
      'Al restaurar la conexión, la llamada se recupera sola del lado del paciente.',
      'Documentar el comportamiento REAL observado en la causa, sea o no el esperado — este escenario es de descubrimiento.',
    ],
    notas: 'Complementa a ESC-36 (caída total de la conexión del paciente): aquí la red no se corta, solo se degrada. Registrar en la causa qué método de simulación (a/b/c/d) se usó, ya que puede afectar la reproducibilidad.',
  },
  {
    id: 'ESC-45',
    area: 'Cuelgue y desconexión',
    titulo: 'Conexión lenta de AMBOS extremos (médico y paciente)',
    objetivo: 'Documentar el peor caso: médico y paciente con conexión degradada simultáneamente, y verificar que la consulta se pueda completar de forma mínima (al menos audio) sin que el sistema falle catastróficamente.',
    configuracionInicial: [
      'Consulta de video en curso.',
      'Aplicar simultáneamente el throttling del médico (ESC-43: DevTools "Slow 3G") y el del paciente (ESC-44: una de las opciones a/b/c/d).',
    ],
    pasos: [
      { actor: MEDICO, accion: 'Activar el throttling "Slow 3G" en DevTools.' },
      { actor: PACIENTE, accion: 'Activar, en paralelo, la simulación de red lenta elegida en su dispositivo.' },
      { actor: MEDICO, accion: 'Con ambas redes degradadas, intentar mantener un intercambio verbal mínimo con el paciente (aunque sea solo audio) y observar si la llamada se sostiene o se cae por completo.' },
      { actor: MEDICO, accion: 'Intentar finalizar la consulta y registrar una disposición, verificando que el guardado no se pierde por la lentitud de red.' },
      { actor: MEDICO, accion: 'Quitar el throttling del médico.' },
      { actor: PACIENTE, accion: 'Restaurar su red normal.' },
    ],
    checkpoints: [
      'ESPERADO (criterio a validar): la llamada no se cae de forma catastrófica; en el peor caso se mantiene solo audio en vez de perderse por completo.',
      'ESPERADO (criterio a validar): el médico puede finalizar la consulta y registrar la disposición aún en estas condiciones, sin perder el SOAPE ya escrito.',
      'Ninguno de los dos lados queda en un estado "colgado" sin salida (ambos pueden salir de la sala de alguna forma).',
      'Documentar el comportamiento REAL observado en la causa — este escenario es de descubrimiento y probablemente revele el peor caso real del sistema.',
    ],
    notas: 'Combina ESC-43 + ESC-44. Es el escenario más exigente de la sección; útil para decidir si se necesita un modo "solo audio" explícito ante mala conexión de ambos lados.',
  },

  // ───────────────────────────────────────────────────────────────────────
  // H. Finalización y resultado en el móvil
  // ───────────────────────────────────────────────────────────────────────
  {
    id: 'ESC-38',
    area: 'Finalización y resultado en el móvil',
    titulo: 'Finalización con disposición',
    objetivo: 'Verificar que el médico puede finalizar la consulta con cada tipo de disposición y que el estado queda correcto.',
    configuracionInicial: ['Consulta en curso.'],
    pasos: [
      { actor: MEDICO, accion: 'Finalizar la consulta eligiendo disposición "Atendida" y confirmar.' },
      { actor: MEDICO, accion: 'Repetir con otra consulta de prueba usando "No contestó" (finalizar consulta es una acción exclusiva del médico asignado — el backend rechaza a cualquier otro rol).' },
      { actor: MEDICO, accion: 'Repetir con otra consulta de prueba usando "Falla técnica".' },
      { actor: SUPERVISOR, accion: 'Verificar en el Monitor/Historial que cada una quedó con el estado y disposición correctos.' },
    ],
    checkpoints: [
      'Cada disposición se guarda correctamente en la consulta.',
      'El estado final de la consulta es coherente con la disposición elegida (ej. "finalizada").',
      'La sala libera el cupo correspondiente en cada caso.',
    ],
  },
  {
    id: 'ESC-39',
    area: 'Finalización y resultado en el móvil',
    titulo: 'Resultado y resumen para el paciente (aparece en el Historial)',
    objetivo: 'Verificar que el paciente ve el resultado de su consulta en el Historial de Teleconsultas casi de inmediato, con el resumen y datos del médico.',
    configuracionInicial: ['Una consulta recién finalizada como "Atendida" con un SOAPE generado.'],
    pasos: [
      { actor: PACIENTE, accion: 'En Inicio, abrir la tarjeta "Teleconsulta médica" y tocar el botón "Historial".' },
      { actor: PACIENTE, accion: 'En la lista "Mis Teleconsultas", ubicar la consulta recién finalizada y tocarla para abrir el detalle.' },
      { actor: PACIENTE, accion: 'Verificar el resumen de la atención, el nombre del médico y la disposición.' },
      { actor: MEDICO, accion: 'Desde el portal, abrir el detalle de esa misma consulta (ConsultaDetallePage) y verificar que la sección "Grabación de la consulta" está disponible.' },
      { actor: MEDICO, accion: 'Reproducir la grabación con el reproductor de audio embebido y confirmar que se escucha correctamente.' },
      { actor: MEDICO, accion: 'Usar el botón de descarga y confirmar que el archivo descarga correctamente.' },
    ],
    checkpoints: [
      'La consulta aparece en la lista "Mis Teleconsultas" casi de inmediato tras finalizar.',
      'El resumen es legible y comprensible para el paciente (no jerga clínica sin explicar, no JSON).',
      'El médico y la disposición se muestran correctamente.',
      'La grabación está disponible en el detalle de la consulta del portal, se reproduce sin error y se puede descargar.',
    ],
    notas: 'Complementa a ESC-40, que valida específicamente que una edición manual del médico en el portal se refleje tal cual en este mismo detalle. IMPORTANTE: la grabación NO es accesible para el paciente en la app móvil — se confirmó en código que ni el tipo de datos del móvil expone un campo de grabación, ni hay ninguna Cloud Function ni regla de Storage que dé acceso a ese rol (`obtenerUrlGrabacion` solo autoriza admin/supervisor/médico tratante). Por eso la verificación de "se escucha y se puede descargar" se hace desde el portal (médico/supervisor/admin), no desde el móvil. Si se desea que el paciente también pueda acceder a su propia grabación, es una funcionalidad nueva pendiente de definir y construir, no un defecto de este escenario.',
  },
  {
    id: 'ESC-40',
    area: 'Finalización y resultado en el móvil',
    titulo: 'Detalle de teleconsulta — resumen del paciente editado por el médico',
    objetivo: 'Verificar que el resumen del paciente que el médico genera/edita en el portal MinSalud (VideoCallPageV2, pestaña "Resumen para el paciente") se refleja tal cual en el detalle de la teleconsulta en la app móvil.',
    configuracionInicial: [
      'Una consulta en curso, próxima a finalizar, donde el médico vaya a editar manualmente el resumen para el paciente (no dejar solo el texto generado por IA).',
    ],
    pasos: [
      { actor: MEDICO, accion: 'En VideoCallPageV2, ir a la pestaña "Resumen para el paciente" y modificar el texto (agregar una frase o dato distintivo, fácil de identificar luego).' },
      { actor: MEDICO, accion: 'Finalizar la consulta.' },
      { actor: PACIENTE, accion: 'En Inicio, abrir la tarjeta "Teleconsulta médica" → botón "Historial" → seleccionar la teleconsulta recién finalizada de la lista "Mis Teleconsultas".' },
      { actor: PACIENTE, accion: 'En el detalle, revisar la sección "Resumen de su atención".' },
    ],
    checkpoints: [
      'El detalle de la teleconsulta muestra la información de la consulta (médico, sala, duración, motivo, disposición).',
      'La sección "Resumen de su atención" muestra EXACTAMENTE el texto que el médico editó en el portal (no la versión original generada por IA, ni un resumen distinto).',
    ],
  },

  // ───────────────────────────────────────────────────────────────────────
  // I. Notificaciones
  // ───────────────────────────────────────────────────────────────────────
  {
    id: 'ESC-41',
    area: 'Notificaciones',
    titulo: 'Notificación de prueba y campaña',
    objetivo: 'Verificar el envío de una notificación de prueba a dispositivos registrados y una campaña real.',
    configuracionInicial: [
      'Sesión iniciada como "Gestión de notificaciones" — es el único rol dueño de este módulo (el Administrador tiene acceso técnico de respaldo, pero no es quien opera Notificaciones normalmente).',
      'Al menos un usuario de prueba con token FCM registrado (Android y/o iOS producción).',
    ],
    pasos: [
      { actor: NOTIFICADOR, accion: 'Ir a Notificaciones → Dispositivos de prueba → enviar notificación de prueba.' },
      { actor: PACIENTE, accion: 'Verificar la recepción en el dispositivo Android.' },
      { actor: PACIENTE, accion: 'Verificar la recepción en el dispositivo iOS (build de producción; los builds de desarrollo pueden no recibir push por el entorno APNs sandbox).' },
      { actor: NOTIFICADOR, accion: 'Crear y enviar una campaña real a un segmento de prueba.' },
      { actor: NOTIFICADOR, accion: 'Revisar las estadísticas de la campaña (entregados/fallidos).' },
    ],
    checkpoints: [
      'La notificación de prueba llega a Android.',
      'La notificación llega a iOS SOLO si el build es de producción (documentar si no llega en build de desarrollo — es una limitante conocida, no un defecto).',
      'Las estadísticas de la campaña reflejan resultados coherentes con lo recibido realmente.',
    ],
  },

  // ───────────────────────────────────────────────────────────────────────
  // J. Carga
  // ───────────────────────────────────────────────────────────────────────
  {
    id: 'ESC-42',
    area: 'Carga',
    titulo: 'Concurrencia básica de solicitudes',
    objetivo: 'Verificar que varias solicitudes simultáneas se admiten de forma ordenada, sin superar la capacidad ni duplicar posiciones.',
    configuracionInicial: [
      'Sala con capacidad conocida (ej. 2 médicos, tiempo 15 min, cupos fijos = 20).',
      'Preferible ejecutar con el harness automático de e2e (`npm run test:e2e:estres`) además de la prueba manual con varios dispositivos/usuarios reales si es posible.',
    ],
    pasos: [
      { actor: SUPERVISOR, accion: 'Coordinar 5–10 pacientes de prueba para solicitar cupo casi al mismo tiempo.' },
      { actor: PACIENTE, accion: 'Cada paciente solicita entrar a la cola dentro de una ventana de ~10 segundos.' },
      { actor: SUPERVISOR, accion: 'Verificar en Monitor en vivo que el número de admitidos coincide con la capacidad y no la supera.' },
      { actor: SUPERVISOR, accion: 'Verificar que las posiciones en cola son consistentes (sin duplicados, sin saltos).' },
    ],
    checkpoints: [
      'Ningún paciente queda con una posición duplicada o incorrecta.',
      'La cantidad de admitidos no supera la capacidad configurada.',
      'No se observan errores 500/timeout en las solicitudes durante la ventana de concurrencia.',
    ],
  },
];

/** Busca un escenario por id. */
export function getEscenario(id: string): EscenarioPrueba | undefined {
  return ESCENARIOS.find((e) => e.id === id);
}

/** Agrupa los escenarios por área, respetando el orden de AREAS_ORDEN. */
export function agruparPorArea(): Array<{ area: string; escenarios: EscenarioPrueba[] }> {
  return AREAS_ORDEN.map((area) => ({
    area,
    escenarios: ESCENARIOS.filter((e) => e.area === area),
  })).filter((g) => g.escenarios.length > 0);
}
